import { useState, useRef, useEffect, useMemo, Fragment } from 'react';
import { useConnector } from '../../contexts/ConnectorContext';
import { useOrderDetail } from '../../hooks/useOrderDetail';
import { DatePickerInput } from '../DatePickerInput';
import { OrderStatusPill } from './OrderStatusPill';
import {
  OrderStatus,
  OrderCostAllocation,
  OrderCurrency,
  OrderPriority,
  ApprovalStage,
  ApprovalDecision,
  isOverdue,
} from '../../types/ordering.types';
import type { Order, OrderData, NewOrderData, COSHHEntry, NewLineItemData, OrderApprovalLog, OrderComment } from '../../types/ordering.types';
import type { Person, Project } from '../../types/timesheet.types';
import { OrderWorkflowTriggerType } from '../../types/connector.types';
import {
  ORDER_SELF_APPROVAL_LIMIT,
  ORDER_CEO_CFO_THRESHOLD,
} from '../../utils/constants';

interface Props {
  orderId: string | null;
  currentPerson: Person;
  people?: Person[];
  onBack: () => void;
  onSubmitted?: () => void;
  onCreated?: (order: Order) => void;
  onOrderChanged?: () => void;
}

interface DraftLineItem {
  id: string;
  itemDescription: string;
  sku: string;
  itemLink: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice: number;
  subtotal: number;
  isChemical: boolean;
  coshhRef: string;
}


/**
 * Determines the current pending financial approver dynamically on every visit.
 *
 * Step 1 — Direct LM always approves first. Returns them if they haven't yet.
 * Step 2 — Once LM approved, check their purchaseApprovalLimit:
 *   • Order ≤ limit  → chain complete, no additional approver (return null).
 *   • Order > limit  → escalate to the next level up.
 * Step 3 — Walk UP the hierarchy ONE LEVEL AT A TIME.
 *   Each intermediate manager must individually approve before the next level is reached.
 *   The chain ends when a manager whose limit covers the order value has approved.
 *   If the top of the hierarchy is reached with no sufficient approver → 'no-approver'.
 */
function findCurrentFinancialApprover(
  orderLMEmail: string,
  orderValue: number,
  people: Person[],
  log: Pick<OrderApprovalLog, 'approvalStage' | 'approverEmail' | 'decision'>[],
  defaultLimit: number,
): { email: string; name: string; isDirectLM: boolean } | null | 'no-approver' {
  const lmEmail = orderLMEmail.toLowerCase().trim();
  if (!lmEmail) return null;

  const lmPerson = people.find(p => p.m365Email.toLowerCase().trim() === lmEmail);
  if (!lmPerson) return null;

  // Step 1: has the direct LM approved?
  const lmApproved = log.some(
    l => l.approvalStage === ApprovalStage.LineManager &&
         l.approverEmail.toLowerCase().trim() === lmEmail &&
         l.decision === ApprovalDecision.Approved
  );
  if (!lmApproved) {
    return { email: lmPerson.m365Email, name: lmPerson.fullName, isDirectLM: true };
  }

  // Step 2: LM approved — is their limit sufficient?
  const lmLimit = lmPerson.purchaseApprovalLimit ?? defaultLimit;
  if (orderValue <= lmLimit) return null;

  // Step 3: walk UP the hierarchy one level at a time.
  // Every intermediate manager must approve before we advance to the next.
  // The chain ends when a manager with sufficient limit has approved.
  const visited = new Set<string>([lmEmail]);
  let cur = lmPerson.lineManagerEmail?.toLowerCase().trim() ?? '';
  for (let depth = 0; depth < 20; depth++) {
    if (!cur || visited.has(cur)) break;
    visited.add(cur);
    const person = people.find(p => p.m365Email.toLowerCase().trim() === cur);
    if (!person) break;

    const hasApproved = log.some(
      l => l.approvalStage === ApprovalStage.EscalatedApproval &&
           l.approverEmail.toLowerCase().trim() === cur &&
           l.decision === ApprovalDecision.Approved
    );

    if (!hasApproved) {
      // This person has not approved yet — they are the current pending approver
      return { email: person.m365Email, name: person.fullName, isDirectLM: false };
    }

    // This person has approved — check whether their limit was sufficient to end the chain
    const limit = person.purchaseApprovalLimit ?? Infinity;
    if (orderValue <= limit) return null;

    // Insufficient limit — continue to the next level up
    cur = person.lineManagerEmail?.toLowerCase().trim() ?? '';
  }
  return 'no-approver';
}

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
}

function fmtDateTime(d: string | null | undefined) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
}

const CAT_NAMES: Record<OrderCostAllocation, string> = {
  [OrderCostAllocation.ProjectCost]:  'Project Cost',
  [OrderCostAllocation.HealthSafety]: 'Health & Safety',
  [OrderCostAllocation.Overhead]:     'Overhead / G&A',
  [OrderCostAllocation.Capex]:        'Capex (Asset)',
};

const CATEGORY_CARDS: { cat: OrderCostAllocation; icon: React.ReactNode; desc: string }[] = [
  {
    cat: OrderCostAllocation.ProjectCost,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>,
    desc: 'Billable to a specific project',
  },
  {
    cat: OrderCostAllocation.HealthSafety,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
    desc: 'PPE, safety kit, training',
  },
  {
    cat: OrderCostAllocation.Overhead,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></svg>,
    desc: 'Office, IT, admin, travel',
  },
  {
    cat: OrderCostAllocation.Capex,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
    desc: 'Plant, machinery, assets',
  },
];

const CURR_SYMBOL: Record<string, string> = {
  GBP: '£', EUR: '€', USD: '$', JPY: '¥', NZD: 'NZ$', HUF: 'Ft',
};

const CURRENCIES = [
  { value: OrderCurrency.GBP, label: 'GBP — British Pound' },
  { value: OrderCurrency.EUR, label: 'EUR — Euro' },
  { value: OrderCurrency.USD, label: 'USD — US Dollar' },
  { value: OrderCurrency.JPY, label: 'JPY — Japanese Yen' },
  { value: OrderCurrency.NZD, label: 'NZD — New Zealand Dollar' },
  { value: OrderCurrency.HUF, label: 'HUF — Hungarian Forint' },
];

function looksLikeUrl(v: string): boolean {
  return /^https?:\/\/.+/i.test(v.trim()) || /^www\..+\..+/i.test(v.trim());
}

function normaliseUrl(v: string): string {
  const t = v.trim();
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

const newDraftLine = (): DraftLineItem => ({
  id: `li-${Date.now()}-${Math.random()}`,
  itemDescription: '',
  sku: '',
  itemLink: '',
  quantity: 1,
  unitOfMeasure: '',
  unitPrice: 0,
  subtotal: 0,
  isChemical: false,
  coshhRef: '',
});

type UnifiedEntry =
  | { kind: 'system';   text: string; ts: string }
  | { kind: 'approval'; log: OrderApprovalLog }
  | { kind: 'comment';  comment: OrderComment };

interface FYIPickerModalProps {
  people: Person[];
  selected: string[];
  search: string;
  onSearchChange: (v: string) => void;
  onToggle: (email: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  isSaving: boolean;
}

function FYIPickerModal({ people, selected, search, onSearchChange, onToggle, onClose, onConfirm, isSaving }: FYIPickerModalProps) {
  const avatarGradient = (name: string) => {
    const palettes = [
      'linear-gradient(135deg,#6366f1,#8b5cf6)',
      'linear-gradient(135deg,#0ea5e9,#6366f1)',
      'linear-gradient(135deg,#10b981,#0ea5e9)',
      'linear-gradient(135deg,#f59e0b,#ef4444)',
      'linear-gradient(135deg,#ec4899,#8b5cf6)',
      'linear-gradient(135deg,#14b8a6,#10b981)',
    ];
    return palettes[(name.charCodeAt(0) + (name.charCodeAt(1) ?? 0)) % palettes.length];
  };
  const activePeople = people.filter(p => p.active && p.m365Email);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? activePeople.filter(p => p.fullName.toLowerCase().includes(q) || p.m365Email.toLowerCase().includes(q))
    : activePeople;
  const selectedLower = selected.map(e => e.toLowerCase());

  return (
    <div className="fyi-picker-overlay" onClick={onClose}>
      <div className="fyi-picker-modal" onClick={e => e.stopPropagation()}>
        <div className="fyi-picker-header">
          <div className="fyi-picker-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 18, height: 18 }}>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Select FYI Viewers
          </div>
          <button className="fyi-picker-close" onClick={onClose} type="button">×</button>
        </div>
        <div className="fyi-picker-search-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 15, height: 15, flexShrink: 0, color: 'var(--text-dim)' }}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="fyi-picker-search"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            autoFocus
          />
          {search && <button type="button" className="fyi-picker-clear" onClick={() => onSearchChange('')}>×</button>}
        </div>
        <div className="fyi-picker-list">
          {filtered.length === 0 && (
            <div className="fyi-picker-empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 28, height: 28, marginBottom: 8, opacity: 0.35 }}>
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <div>No people found</div>
            </div>
          )}
          {filtered.map(p => {
            const checked = selectedLower.includes(p.m365Email.toLowerCase());
            const initials = p.fullName.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
            return (
              <label key={p.m365Email} className={`fyi-picker-row${checked ? ' fyi-picker-row--checked' : ''}`}>
                <div className="fyi-picker-check-wrap">
                  <input type="checkbox" className="fyi-picker-check" checked={checked} onChange={() => onToggle(p.m365Email)} />
                </div>
                <div className="fyi-picker-avatar" style={{ background: avatarGradient(p.fullName) }}>{initials}</div>
                <div className="fyi-picker-info">
                  <div className="fyi-picker-name">{p.fullName}</div>
                  <div className="fyi-picker-email">{p.m365Email}</div>
                </div>
                {checked && (
                  <svg className="fyi-picker-tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 16, height: 16, flexShrink: 0 }}>
                    <polyline points="20 6 9 17 4 12" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </label>
            );
          })}
        </div>
        <div className="fyi-picker-footer">
          <span className="fyi-picker-count">
            {selected.length > 0 ? <><strong>{selected.length}</strong> selected</> : 'No one selected'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" type="button" onClick={onConfirm} disabled={isSaving}>
              {isSaving
                ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Saving…</>
                : <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}><polyline points="20 6 9 17 4 12" /></svg>
                    Apply{selected.length > 0 ? ` (${selected.length})` : ''}
                  </>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OrderDetail({ orderId, currentPerson, people, onBack, onSubmitted, onCreated, onOrderChanged }: Props) {
  const connector = useConnector();
  const {
    order, lineItems, approvalLog, comments, attachments,
    isLoading, error, isSaving,
    addComment, addApproval, updateOrder, uploadAttachment, downloadAttachment, reload,
  } = useOrderDetail(orderId);

  // ── Draft edit mode state ──────────────────────────────────────────────────
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [draftProjects, setDraftProjects] = useState<Project[]>([]);
  const [draftCOSHH, setDraftCOSHH] = useState<COSHHEntry[]>([]);
  const [coshhSearchTerms, setCoshhSearchTerms] = useState<Record<string, string>>({});
  const [coshhOpenIds, setCoshhOpenIds] = useState<Record<string, boolean>>({});
  const coshhInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [coshhScrollTick, setCoshhScrollTick] = useState(0);
  const [focusedLinkId, setFocusedLinkId] = useState<string | null>(null);
  const [costCentreOptions, setCostCentreOptions] = useState<string[]>([]);
  const [unitOfMeasureOptions, setUnitOfMeasureOptions] = useState<string[]>([]);
  const [draftForm, setDraftForm] = useState({
    costAllocation: OrderCostAllocation.ProjectCost,
    projectCode: '',
    projectName: '',
    supplier: '',
    priority: OrderPriority.Standard,
    currency: OrderCurrency.GBP,
    sterlingEquivalent: '',
    requiredByDate: '',
    title: '',
    notes: '',
  });
  const [draftLineItems, setDraftLineItems] = useState<DraftLineItem[]>([newDraftLine()]);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftActiveAction, setDraftActiveAction] = useState<'save' | 'submit' | null>(null);
  const [draftAttachments, setDraftAttachments] = useState<File[]>([]);
  const draftFileRef = useRef<HTMLInputElement>(null);
  const [draftFyiViewers, setDraftFyiViewers] = useState<string[]>([]);
  const [showFYIPickerDraft, setShowFYIPickerDraft] = useState(false);
  const draftInitializedRef = useRef(false);

  // Initialize draft form from loaded order data (only once).
  // Runs for Draft/Rejected (requester flow) and for any status when OM group member edits.
  useEffect(() => {
    if (!order || draftInitializedRef.current) return;
    draftInitializedRef.current = true;
    setDraftForm({
      costAllocation: order.costAllocation,
      projectCode: order.projectCode,
      projectName: order.projectName,
      supplier: order.supplier,
      priority: order.priority,
      currency: order.currency,
      sterlingEquivalent: order.sterlingEquivalent != null ? String(order.sterlingEquivalent) : '',
      requiredByDate: order.requiredByDate ?? '',
      title: order.title,
      notes: order.notes,
    });
    if (lineItems.length > 0) {
      setDraftLineItems(lineItems.map(li => ({
        id: li.id,
        itemDescription: li.itemDescription,
        sku: li.sku,
        itemLink: li.itemLink,
        quantity: li.quantity,
        unitOfMeasure: li.unitOfMeasure,
        unitPrice: li.unitPrice,
        subtotal: li.subtotal,
        isChemical: li.isChemical,
        coshhRef: li.coshhRef,
      })));
    }
    setDraftFyiViewers([...(order.fyiViewerEmails ?? [])]);
  }, [order, lineItems]);

  // Load projects + COSHH for new order form, draft edit form, and OM full-edit mode
  useEffect(() => {
    connector.getAllActiveProjects().then(setDraftProjects).catch(() => {});
    connector.getCOSHHRegister().then(setDraftCOSHH).catch(() => {});
    connector.getCostCentreOptions().then(setCostCentreOptions).catch(() => {});
    connector.getUnitOfMeasureOptions().then(setUnitOfMeasureOptions).catch(() => {});
  }, [connector, orderId]);

  useEffect(() => {
    const anyOpen = Object.values(coshhOpenIds).some(Boolean);
    if (!anyOpen) return;
    const handler = () => setCoshhScrollTick(t => t + 1);
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [coshhOpenIds]);

  // OM group membership from dedicated SP list
  const [omGroupEmails, setOMGroupEmails] = useState<Set<string>>(new Set());
  useEffect(() => {
    connector.getOrderMgmtGroupMembers().then(setOMGroupEmails).catch(() => {});
  }, [connector]);

  // Auto-transition: order stuck in Requested after LM approval with no escalation path
  useEffect(() => {
    if (!order || !people?.length || order.status !== OrderStatus.Requested) return;
    const result = findCurrentFinancialApprover(
      order.lineManagerEmail,
      order.sterlingEquivalent ?? order.totalAmount,
      people,
      approvalLog,
      ORDER_SELF_APPROVAL_LIMIT,
    );
    if (result !== 'no-approver') return;
    updateOrder({ status: OrderStatus.PendingEscalatedApproval, escalatedApproverEmail: '', escalatedApproverName: '' }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, order?.status]);

  // ── View mode state ────────────────────────────────────────────────────────
  const [commentText, setCommentText] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [ceoEmailFile, setCeoEmailFile] = useState<File | null>(null);
  const [omFields, setOMFields] = useState({ poNumber: '', costCentreName: '', expectedDeliveryDate: '', actualDeliveryDate: '', sterlingEquivalent: '' });
  const [confirmDeleteFYIEmail, setConfirmDeleteFYIEmail] = useState<string | null>(null);
  const [showOMPanel, setShowOMPanel] = useState(false);
  const [omEditOpen, setOMEditOpen] = useState(false);
  const [showFYIPickerLive, setShowFYIPickerLive] = useState(false);
  const [fyiPickerSearch, setFyiPickerSearch] = useState('');
  const [fyiPickerSelected, setFyiPickerSelected] = useState<string[]>([]);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showForReviewConfirm, setShowForReviewConfirm] = useState(false);
  const [showNotifyOMConfirm, setShowNotifyOMConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const submitConfirmFnRef = useRef<(() => void) | null>(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [showPartialDeliveryConfirm, setShowPartialDeliveryConfirm] = useState(false);
  const [showDeliveredConfirm, setShowDeliveredConfirm] = useState(false);
  const [successToast, setSuccessToast] = useState<{ title: string; sub: string; icon: 'check' | 'reject' | 'park' | 'cancel' | 'deliver' | 'partial' | 'withdraw' | 'submit' } | null>(null);
  const successToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successToastCallbackRef = useRef<(() => void) | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const attachFileRef = useRef<HTMLInputElement>(null);

  // Prevent browser from opening dropped files in a new tab anywhere on the page
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  // ── Unified timeline (approval log + comments merged) ────────────────────
  const unifiedTimeline = useMemo((): UnifiedEntry[] => {
    if (!order) return [];
    const entries: UnifiedEntry[] = [];
    for (const log of approvalLog) entries.push({ kind: 'approval', log });
    for (const comment of comments) entries.push({ kind: 'comment', comment });
    const ts = (e: UnifiedEntry) =>
      e.kind === 'approval' ? e.log.approvalDate :
      e.kind === 'comment'  ? e.comment.commentDate : e.ts;
    return entries.sort((a, b) => (ts(a) < ts(b) ? -1 : ts(a) > ts(b) ? 1 : 0));
  }, [order, approvalLog, comments]);

  // ── Shared draft form helpers (used by both new-order and draft-edit modes) ─
  const setDraftField = <K extends keyof typeof draftForm>(key: K, value: typeof draftForm[K]) =>
    setDraftForm(f => ({ ...f, [key]: value }));

  const updateDraftLine = (id: string, updates: Partial<DraftLineItem>) => {
    setDraftLineItems(prev => prev.map(li => {
      if (li.id !== id) return li;
      const merged = { ...li, ...updates };
      merged.subtotal = parseFloat((merged.quantity * merged.unitPrice).toFixed(2));
      return merged;
    }));
  };

  const validateDraft = (): string | null => {
    if (!draftForm.title.trim()) return 'Order title is required.';
    if (draftForm.costAllocation === OrderCostAllocation.ProjectCost && !draftForm.projectCode) return 'Please select a project.';
    if (draftLineItems.length === 0) return 'At least one line item is required.';
    for (const li of draftLineItems) {
      if (!li.itemDescription.trim()) return 'All line items must have a description.';
      if (li.quantity <= 0) return 'Quantity must be greater than 0.';
      if (li.unitPrice < 0) return 'Unit price cannot be negative.';
      if (li.isChemical && !li.coshhRef) return 'Chemical lines must reference a COSHH entry.';
      if (li.isChemical && li.coshhRef === '__request') return 'This chemical is not on the COSHH register. Submit a registration request to H&S first.';
    }
    if (!draftForm.requiredByDate) return 'Required by date is required.';
    if (draftForm.currency !== OrderCurrency.GBP && !draftForm.sterlingEquivalent) return 'Sterling equivalent is required for non-GBP orders.';
    return null;
  };

  if (isLoading) return <div className="ord-view-content ord-empty"><div className="spinner" /> Loading order…</div>;

  // ── New order mode (no existing order) ────────────────────────────────────
  const isNewOrder = !orderId;
  if (isNewOrder) {
    const newTotalAmount = draftLineItems.reduce((s, li) => s + li.subtotal, 0);
    const newSterlingValue = draftForm.currency === OrderCurrency.GBP ? newTotalAmount : (parseFloat(draftForm.sterlingEquivalent) || 0);
    const newHasChemical = draftLineItems.some(li => li.isChemical);
    const cs = CURR_SYMBOL[draftForm.currency] ?? '£';
    const fmt = (v: number) => `${cs}${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const buildNewOrderData = (status: OrderStatus): NewOrderData => ({
      title: draftForm.title.trim(),
      costAllocation: draftForm.costAllocation,
      projectCode: draftForm.projectCode || undefined,
      projectName: draftForm.projectName || undefined,
      supplier: draftForm.supplier.trim(),
      priority: draftForm.priority,
      currency: draftForm.currency,
      totalAmount: newTotalAmount,
      sterlingEquivalent: draftForm.currency === OrderCurrency.GBP ? newTotalAmount : (parseFloat(draftForm.sterlingEquivalent) || null),
      requiredByDate: draftForm.requiredByDate || null,
      notes: draftForm.notes.trim(),
      hasChemicalLines: newHasChemical,
      lineManagerEmail: currentPerson.lineManagerEmail,
      lineManagerName: currentPerson.lineManager,
      requesterEmail: currentPerson.m365Email,
      requesterName: currentPerson.fullName,
      status,
    });

    const saveNewLineItems = async (spId: number, orderRef: string) => {
      const items: NewLineItemData[] = draftLineItems.map((li, idx) => ({
        orderId: spId,
        itemDescription: li.itemDescription,
        sku: li.sku,
        itemLink: li.itemLink,
        quantity: li.quantity,
        unitOfMeasure: li.unitOfMeasure,
        unitPrice: li.unitPrice,
        subtotal: li.subtotal,
        isChemical: li.isChemical,
        coshhRef: li.coshhRef,
        lineOrder: idx + 1,
      }));
      await connector.saveOrderLineItems(spId, items);
      for (const f of draftAttachments) {
        await connector.uploadOrderAttachment(spId, orderRef, f, 'quote');
      }
    };

    const handleCreateDraft = async () => {
      setDraftError(null);
      setShowSubmitConfirm(false);
      submitConfirmFnRef.current = null;
      setDraftActiveAction('save');
      setIsDraftSaving(true);
      try {
        const newOrder = await connector.createOrder(buildNewOrderData(OrderStatus.Draft));
        await saveNewLineItems(newOrder.spId, newOrder.orderRef);
        if (draftFyiViewers.length > 0) await connector.updateOrder(String(newOrder.spId), { fyiViewerEmails: draftFyiViewers });
        onCreated?.(newOrder);
      } catch (e) {
        setDraftError(e instanceof Error ? e.message : 'Failed to save draft.');
      } finally {
        setIsDraftSaving(false);
        setDraftActiveAction(null);
      }
    };

    const handleCreateAndSubmit = async () => {
      const err = validateDraft();
      if (err) { setDraftError(err); return; }
      setDraftError(null);
      setDraftActiveAction('submit');
      setIsDraftSaving(true);
      try {
        const limit = currentPerson.purchaseApprovalLimit ?? ORDER_SELF_APPROVAL_LIMIT;
        const effectiveSelfLimit = Math.max(ORDER_SELF_APPROVAL_LIMIT, limit);
        const isSelfApproved = !newHasChemical && newSterlingValue <= effectiveSelfLimit;
        // Chemical orders always go to Chemical Approver first, then LM if above limit
        const newStatus = isSelfApproved
          ? OrderStatus.OMApproved
          : newHasChemical
          ? OrderStatus.PendingChemApproval
          : OrderStatus.Requested;
        const orderDate = new Date().toISOString().split('T')[0];
        const newOrder = await connector.createOrder({ ...buildNewOrderData(newStatus), orderDate } as NewOrderData & { orderDate: string });
        await saveNewLineItems(newOrder.spId, newOrder.orderRef);
        if (draftFyiViewers.length > 0) await connector.updateOrder(String(newOrder.spId), { fyiViewerEmails: draftFyiViewers });
        if (isSelfApproved) {
          await connector.updateOrder(String(newOrder.spId), { isLocked: true, isSelfApproved: true });
          await connector.addApprovalDecision({
            orderId: newOrder.spId,
            approvalStage: ApprovalStage.SelfApproval,
            approverName: currentPerson.fullName,
            approverEmail: currentPerson.m365Email,
            decision: ApprovalDecision.Approved,
            note: 'Self-approved (order within self-approval limit)',
          });
        }
        await connector.addOrderComment({
          orderId: newOrder.spId,
          authorName: currentPerson.fullName,
          authorEmail: currentPerson.m365Email,
          commentText: isSelfApproved
            ? 'Order self-approved and sent to OM for processing.'
            : newHasChemical
            ? 'Order submitted for chemical approval.'
            : 'Order submitted for approval.',
        });
        const _newOrderAmount = newOrder.sterlingEquivalent ?? newOrder.totalAmount;
        const _baseTrigger = { orderRef: newOrder.orderRef, orderId: newOrder.spId, orderTitle: newOrder.title, orderAmount: _newOrderAmount, requesterEmail: currentPerson.m365Email, requesterName: currentPerson.fullName, projectName: newOrder.projectName };
        connector.triggerOrderWorkflow({ ..._baseTrigger, triggerType: OrderWorkflowTriggerType.OrderSubmitted, recipientEmail: currentPerson.m365Email, recipientName: currentPerson.fullName, templateName: 'order-submitted' }).catch(() => {});
        if (newHasChemical) {
          const _chem = (people ?? []).find(p => p.isChemicalApprover);
          if (_chem) connector.triggerOrderWorkflow({ ..._baseTrigger, triggerType: OrderWorkflowTriggerType.ChemicalApprovalRequest, recipientEmail: _chem.m365Email, recipientName: _chem.fullName, templateName: 'chemical-approval-request' }).catch(() => {});
        } else if (isSelfApproved) {
          const _omEmails = Array.from(omGroupEmails).join(';');
          connector.triggerOrderWorkflow({ ..._baseTrigger, triggerType: OrderWorkflowTriggerType.OrderSelfApprovedToOM, recipientEmail: _omEmails, recipientName: 'OM Group', templateName: 'order-self-approved-to-om' }).catch(() => {});
        } else {
          connector.triggerOrderWorkflow({ ..._baseTrigger, triggerType: OrderWorkflowTriggerType.LMApprovalRequest, recipientEmail: currentPerson.lineManagerEmail, recipientName: currentPerson.lineManager, templateName: 'lm-approval-request' }).catch(() => {});
        }
        onSubmitted ? onSubmitted() : onCreated?.(newOrder);
      } catch (e) {
        setDraftError(e instanceof Error ? e.message : 'Failed to submit order.');
      } finally {
        setIsDraftSaving(false);
        setDraftActiveAction(null);
      }
    };

    return (
      <>
      <div className="ord-view-content">
        <div className="ord-detail-hdr">
          <div>
            <div className="ord-detail-title">New Order</div>
            <div className="ord-detail-sub">Fill in the details below to raise a purchase order</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 14, height: 14 }}>
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Cancel
          </button>
        </div>
        <div className="validation-box">
          <div className="vh">What happens when you submit</div>
          <ul>
            <li><strong>Self-approval</strong> — Orders within your approval limit are automatically approved and sent directly to the Order Management (OM) team.</li>
            <li><strong>Line Manager</strong> — Orders exceeding your approval limit are routed to your line manager for approval.</li>
            <li><strong>C-Level Approval</strong> — Orders up to £10,000 require approval from a C-level executive.</li>
            <li><strong>CEO &amp; CFO Approval</strong> — Orders over £10,000 require approval from both the CEO and CFO.</li>
            <li><strong>Chemical Approval</strong> — All chemical-related orders are first routed to the designated chemical approver.</li>
          </ul>
        </div>
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">1</span>Where should the cost be allocated?</div>
          <div className="cat-picker">
            {CATEGORY_CARDS.map(c => (
              <button key={c.cat} className={`cat-card${draftForm.costAllocation === c.cat ? ' selected' : ''}`} onClick={() => setDraftField('costAllocation', c.cat)} type="button">
                {c.icon}
                <div className="cn">{CAT_NAMES[c.cat]}</div>
                <div className="cd">{c.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">2</span>Order details</div>
          <div className="ord-grid-2">
            <div className="form-field" style={{ display: draftForm.costAllocation === OrderCostAllocation.ProjectCost ? undefined : 'none' }}>
              <label>Project <span className="req">*</span></label>
              <select className="finput" value={draftForm.projectCode} onChange={e => { const proj = draftProjects.find(p => p.projectCode === e.target.value); setDraftField('projectCode', e.target.value); setDraftField('projectName', proj?.projectName ?? ''); }}>
                <option value="">— Select a project —</option>
                {draftProjects.map(p => <option key={p.projectCode} value={p.projectCode}>{p.projectCode} — {p.projectName}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Required by <span className="req">*</span></label>
              <DatePickerInput value={draftForm.requiredByDate} onChange={v => setDraftField('requiredByDate', v)} />
            </div>
            <div className="form-field">
              <label>Supplier</label>
              <input className="finput" placeholder="e.g. RS Components, Dell UK" value={draftForm.supplier} onChange={e => setDraftField('supplier', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Priority</label>
              <select className="finput" value={draftForm.priority} onChange={e => setDraftField('priority', e.target.value as OrderPriority)}>
                <option value={OrderPriority.Standard}>Standard</option>
                <option value={OrderPriority.Urgent}>Urgent</option>
                <option value={OrderPriority.Low}>Low</option>
              </select>
            </div>
          </div>
          <div className="ord-grid-2" style={{ marginTop: 12 }}>
            <div className="form-field">
              <label>Currency <span className="req">*</span></label>
              <select className="finput" value={draftForm.currency} onChange={e => setDraftField('currency', e.target.value as OrderCurrency)}>
                {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            {draftForm.currency !== OrderCurrency.GBP && (
              <div className="form-field">
                <label>Sterling equivalent <span className="req">*</span></label>
                <input className="finput" type="number" step="0.01" min="0" value={draftForm.sterlingEquivalent} onChange={e => setDraftField('sterlingEquivalent', e.target.value)} />
              </div>
            )}
          </div>
          <div className="form-field" style={{ marginTop: 12 }}>
            <label>Order title <span className="req">*</span></label>
            <input className="finput" placeholder="e.g. Replacement laptop for Simon Mitchell" value={draftForm.title} onChange={e => setDraftField('title', e.target.value)} />
          </div>
          <div className="form-field" style={{ marginTop: 12 }}>
            <label>Notes to approver</label>
            <textarea className="fnote" placeholder="Why is this needed? Any context for the approver..." value={draftForm.notes} onChange={e => setDraftField('notes', e.target.value)} />
          </div>
        </div>
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">3</span>Line items</div>
          <div className="ord-li-scroll-wrap">
          <table className="li-table">
            <colgroup><col style={{ width: '4%' }} /><col style={{ width: '20%' }} /><col style={{ width: '21%' }} /><col style={{ width: '8%' }} /><col style={{ width: '10%' }} /><col style={{ width: '8%' }} /><col style={{ width: '20%' }} /></colgroup>
            <thead><tr><th style={{ textAlign: 'center' }}>#</th><th>Description</th><th>SKU</th><th>Qty</th><th>UOM</th><th style={{ textAlign: 'left' }}>Unit price</th><th style={{ textAlign: 'right' }}>Line total</th></tr></thead>
            <tbody>
              {draftLineItems.map((li, idx) => (
                <Fragment key={li.id}>
                <tr>
                  <td style={{ textAlign: 'center', color: 'var(--text-dim)', fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{idx + 1}</td>
                  <td>
                    <div className="li-desc-row">
                      {draftLineItems.length > 1 && (
                        <button className="li-del" type="button" onClick={() => setConfirmDeleteId(li.id)} title="Remove line">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                        </button>
                      )}
                      <input className="li-input" placeholder="Item description (e.g. IPA 99% – item code XYZ)" value={li.itemDescription} onChange={e => updateDraftLine(li.id, { itemDescription: e.target.value })} />
                    </div>
                    <label className="coshh-toggle">
                      <input type="checkbox" checked={li.isChemical} onChange={e => updateDraftLine(li.id, { isChemical: e.target.checked, coshhRef: '' })} />
                      {' '}This line is a chemical (COSHH required)
                    </label>
                    {li.isChemical && (
                      <>
                        {(() => {
                          const term = (coshhSearchTerms[li.id] ?? '').toLowerCase();
                          const filtered = term
                            ? draftCOSHH.filter(c =>
                                c.coshhRef.toLowerCase().includes(term) ||
                                c.chemicalName.toLowerCase().includes(term) ||
                                c.hazardType.toLowerCase().includes(term) ||
                                c.description.toLowerCase().includes(term)
                              )
                            : draftCOSHH;
                          const selected = li.coshhRef && li.coshhRef !== '__request'
                            ? draftCOSHH.find(c => (c.coshhRef || String(c.id)) === li.coshhRef)
                            : null;
                          const isOpen = coshhOpenIds[li.id] ?? false;
                          return (
                            <div className="coshh-search-wrap">
                              <div className="coshh-input-row">
                                <input
                                  ref={el => { coshhInputRefs.current[li.id] = el; }}
                                  className={`coshh-search-input${li.coshhRef === '__request' ? ' missing' : ''}`}
                                  placeholder={selected ? `${selected.chemicalName} - ${selected.hazardType}` : 'Search by chemical name or hazard type…'}
                                  value={coshhSearchTerms[li.id] ?? ''}
                                  onFocus={() => setCoshhOpenIds(p => ({ ...p, [li.id]: true }))}
                                  onBlur={() => setTimeout(() => setCoshhOpenIds(p => ({ ...p, [li.id]: false })), 150)}
                                  onChange={e => {
                                    setCoshhSearchTerms(p => ({ ...p, [li.id]: e.target.value }));
                                    if (e.target.value) updateDraftLine(li.id, { coshhRef: '' });
                                    setCoshhOpenIds(p => ({ ...p, [li.id]: true }));
                                  }}
                                />
                                {li.coshhRef && (
                                  <button className="coshh-clear" type="button" onMouseDown={e => { e.preventDefault(); updateDraftLine(li.id, { coshhRef: '' }); setCoshhSearchTerms(p => ({ ...p, [li.id]: '' })); }}>×</button>
                                )}
                              </div>
                              {li.coshhRef === '__request' && (
                                <div className="coshh-request-note">Not on register — submit a registration request to H&amp;S first.</div>
                              )}
                              {isOpen && (() => { void coshhScrollTick; const r = coshhInputRefs.current[li.id]?.getBoundingClientRect(); const pos = r ? { top: r.bottom, left: r.left, width: Math.max(r.width, 480) } : null; return pos ? (
                                <ul className="coshh-dropdown" style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}>
                                  {filtered.map(c => (
                                    <li key={c.id} onMouseDown={() => { updateDraftLine(li.id, { coshhRef: c.coshhRef || String(c.id) }); setCoshhSearchTerms(p => ({ ...p, [li.id]: '' })); setCoshhOpenIds(p => ({ ...p, [li.id]: false })); }}>
                                      <span className="coshh-dd-ref">{c.chemicalName}</span>
                                      <span className="coshh-dd-hazard">{c.hazardType}</span>
                                    </li>
                                  ))}
                                  {filtered.length === 0 && <li className="coshh-dd-empty">No matches</li>}
                                  <li className="coshh-dd-request" onMouseDown={() => { updateDraftLine(li.id, { coshhRef: '__request' }); setCoshhSearchTerms(p => ({ ...p, [li.id]: '' })); setCoshhOpenIds(p => ({ ...p, [li.id]: false })); window.open('https://evovetech.sharepoint.com/sites/EvoveTechHub/_layouts/15/listforms.aspx?cid=NGQ4MWMxN2EtNDY1My00YjY3LTk0NDItNTI0Mzc0NTY2ZDdm&nav=YzNiZGEzMWYtNjFmOS00ZWJmLTk0NDctOGJlNDhlMTA1NDBk&xsdata=MDV8MDJ8fGJmYWI0NzNkZGE5NjRjODlkNzdjMDhkZWM1NDBmMzQyfDk2MTNkMTQ2ZmE4YjRmZjM5NjY1ZTFmZTY5YzVlYzQ1fDB8MHw2MzkxNjUwNzk4ODMwMTE5NjR8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA5fDF8TDJOb1lYUnpMekU1T2pJeFl6ZGhObVpoTFRnME1qZ3ROREEzT1MxaVlUWTBMVFV3TnprME1HRXpPVE5qTmw5bE1XWmtPVFV6WXkxallXSTNMVFJrTVRrdFlXWTROeTFsTURjMk5qZGpZekV6WVRKQWRXNXhMbWRpYkM1emNHRmpaWE12YldWemMyRm5aWE12TVRjNE1Ea3hNVEU0TmpZek9RPT18NjYwNDM5NjY5MTVmNDNmNjZmMDEwOGRlYzU0MGYzNDJ8YWFiNjRkZmRmZGQ3NGM1MmJjNDc0ODgxMjhkNDQyZjA%3D&sdata=WXBnUHV1WEI4SzZkR0tKM2pkSWk3RkJQWWFNN2lLZ0lFQ3JiME1neGNvND0%3D&ovuser=9613d146-fa8b-4ff3-9665-e1fe69c5ec45%2Cdevops%40coreshare.co.uk', '_blank'); }}>+ Request new COSHH entry via H&amp;S…</li>
                                </ul>
                              ) : null; })()}
                            </div>
                          );
                        })()}
                        {li.coshhRef && li.coshhRef !== '__request' && (() => { const entry = draftCOSHH.find(c => c.coshhRef === li.coshhRef); return entry?.description ? <div className="coshh-description">{entry.description}</div> : null; })()}
                      </>
                    )}
                  </td>
                  <td><input className="li-input li-sku-input1" placeholder="SKU / part number (optional)" value={li.sku} onChange={e => updateDraftLine(li.id, { sku: e.target.value })} /></td>
                  <td><input className="li-input li-qty" type="number" min={1} value={li.quantity} onChange={e => updateDraftLine(li.id, { quantity: parseFloat(e.target.value) || 1 })} /></td>
                  <td>
                    <select className="li-input li-uom" value={li.unitOfMeasure} onChange={e => updateDraftLine(li.id, { unitOfMeasure: e.target.value })}>
                      <option value="">— select —</option>
                      {unitOfMeasureOptions.map(uom => <option key={uom} value={uom}>{uom}</option>)}
                    </select>
                  </td>
                  <td><input className="li-input li-price" type="number" min={0} step="0.01" value={li.unitPrice} onChange={e => updateDraftLine(li.id, { unitPrice: parseFloat(e.target.value) || 0 })} /></td>
                  <td className="li-total">{fmt(li.subtotal)}</td>
                </tr>
                <tr className="li-link-row">
                  <td />
                  <td colSpan={6}>
                    {looksLikeUrl(li.itemLink) && focusedLinkId !== li.id ? (
                      <div className="li-link-bubble">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                        <a href={normaliseUrl(li.itemLink)} target="_blank" rel="noopener noreferrer" title={li.itemLink}>{li.itemLink.length > 80 ? li.itemLink.slice(0, 77) + '…' : li.itemLink}</a>
                        <button type="button" className="li-link-clear" onClick={() => updateDraftLine(li.id, { itemLink: '' })} title="Remove link">×</button>
                      </div>
                    ) : (
                      <input className="li-input li-link-input" placeholder="Paste product link here (optional)" value={li.itemLink} onFocus={() => setFocusedLinkId(li.id)} onBlur={() => setFocusedLinkId(null)} onChange={e => updateDraftLine(li.id, { itemLink: e.target.value })} onPaste={e => { const t = e.clipboardData.getData('text'); if (looksLikeUrl(t)) { e.preventDefault(); updateDraftLine(li.id, { itemLink: t.trim() }); } }} />
                    )}
                  </td>
                </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
          <div className="li-totals">
            <div><span className="lbl">Total</span><span className="val grand">{fmt(newTotalAmount)}</span></div>
          </div>
          </div>{/* ord-li-scroll-wrap */}
          <button className="li-add" type="button" onClick={() => setDraftLineItems(prev => [...prev, newDraftLine()])}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Add line item
          </button>
          {newHasChemical && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>⚠</span> This order contains chemical items — COSHH register compliance will be checked at submission.
            </div>
          )}
        </div>
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">4</span>Attachments</div>
          {draftAttachments.length > 0 && (
            <div className="attach-list" style={{ marginBottom: 8 }}>
              {draftAttachments.map((f, idx) => (
                <div className="attach-row" key={`${f.name}-${idx}`}>
                  <svg className="fi" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  <span className="atname" style={{ color: 'var(--text-muted)' }}>{f.name}</span>
                  <span className="atmeta" style={{ color: 'var(--accent)' }}>Pending</span>
                  <button className="atdel" title="Remove" onClick={() => setDraftAttachments(prev => prev.filter((_, i) => i !== idx))}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div
            className={`attach-drop${isDragOver ? ' drag-over' : ''}`}
            onClick={() => draftFileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDragEnter={e => { e.preventDefault(); dragCounterRef.current += 1; setIsDragOver(true); }}
            onDragLeave={() => { dragCounterRef.current -= 1; if (dragCounterRef.current === 0) setIsDragOver(false); }}
            onDrop={e => {
              e.preventDefault();
              e.stopPropagation();
              dragCounterRef.current = 0;
              setIsDragOver(false);
              const files = Array.from(e.dataTransfer.files);
              if (files.length) setDraftAttachments(prev => [...prev, ...files]);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
            {isDragOver ? 'Drop files here' : 'Drop quote PDFs here or click to browse'}
          </div>
          <input ref={draftFileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg" multiple style={{ display: 'none' }} onChange={e => { const files = Array.from(e.target.files ?? []); if (files.length) setDraftAttachments(prev => [...prev, ...files]); e.target.value = ''; }} />
        </div>
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">5</span>FYI Viewers <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--text-muted)' }}>(optional)</span></div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>People who should be able to view this order but are not part of the approval chain.</div>
          {draftFyiViewers.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {draftFyiViewers.map(email => {
                const fp = (people ?? []).find(x => x.m365Email.toLowerCase() === email.toLowerCase());
                return (
                  <span key={email} className="fyi-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 11, height: 11, flexShrink: 0 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    {fp ? fp.fullName : email}
                    <button className="fyi-chip-del" type="button" title={`Remove ${email}`} onClick={() => setDraftFyiViewers(v => v.filter(e => e !== email))}>×</button>
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => { setFyiPickerSelected([...draftFyiViewers]); setFyiPickerSearch(''); setShowFYIPickerDraft(true); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
              Select People
            </button>
          </div>
        </div>
        {showFYIPickerDraft && <FYIPickerModal people={people ?? []} selected={fyiPickerSelected} search={fyiPickerSearch} onSearchChange={setFyiPickerSearch} onToggle={email => setFyiPickerSelected(prev => prev.map(e=>e.toLowerCase()).includes(email.toLowerCase()) ? prev.filter(e=>e.toLowerCase()!==email.toLowerCase()) : [...prev,email])} onClose={() => setShowFYIPickerDraft(false)} onConfirm={() => { setDraftFyiViewers([...fyiPickerSelected]); setShowFYIPickerDraft(false); }} isSaving={false} />}
        {confirmDeleteId && (() => {
          const target = draftLineItems.find(x => x.id === confirmDeleteId);
          return (
            <div className="li-del-overlay" onClick={() => setConfirmDeleteId(null)}>
              <div className="li-del-modal" onClick={e => e.stopPropagation()}>
                <div className="li-del-modal-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg></div>
                <div className="li-del-modal-title">Remove line item?</div>
                <div className="li-del-modal-desc">{target?.itemDescription?.trim() ? <><span className="li-del-modal-item">"{target.itemDescription}"</span><br />will be permanently removed from this order.</> : 'This line item will be permanently removed from this order.'}</div>
                <div className="li-del-modal-actions">
                  <button className="btn btn-ghost" type="button" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                  <button className="li-del-modal-confirm" type="button" onClick={() => { setDraftLineItems(prev => prev.filter(x => x.id !== confirmDeleteId)); setConfirmDeleteId(null); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                    Yes, remove it
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
        {draftError && <div className="err-banner" style={{ marginBottom: 16 }}>{draftError}</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
          <button className="btn btn-ghost" type="button" onClick={onBack}>Cancel</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" type="button" onClick={handleCreateDraft} disabled={isDraftSaving}>
              {isDraftSaving && draftActiveAction === 'save' ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Save Draft'}
            </button>
            <button className="btn btn-success" type="button" onClick={() => { submitConfirmFnRef.current = handleCreateAndSubmit; setShowSubmitConfirm(true); }} disabled={isDraftSaving}>
              {isDraftSaving && draftActiveAction === 'submit' ? (
                <><div className="spinner" style={{ width: 14, height: 14 }} /> Submitting…</>
              ) : (
                <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 14, height: 14 }}><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>Submit for Approval</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Submit confirmation modal (new order) ─────────────────────────── */}
      {showSubmitConfirm && (
        <div className="li-del-overlay" onClick={() => setShowSubmitConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon" style={{ background: 'rgba(34,197,94,0.1)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--success,#22c55e)" strokeWidth="1.8" style={{ width: 24, height: 24 }}>
                <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" />
              </svg>
            </div>
            <div className="li-del-modal-title">Submit for Approval?</div>
            <div className="li-del-modal-desc">
              Once submitted, your order will be sent for approval. You won't be able to edit it until a decision is made.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowSubmitConfirm(false)}>Cancel</button>
              <button
                className="li-del-modal-confirm"
                style={{ background: 'var(--success,#22c55e)' }}
                disabled={isDraftSaving}
                onClick={() => { setShowSubmitConfirm(false); submitConfirmFnRef.current?.(); }}
              >
                {isDraftSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Submitting…</> : 'Yes, Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  if (error || !order) return <div className="ord-view-content"><div className="err-banner">{error?.message ?? 'Order not found.'}</div></div>;

  const email = currentPerson.m365Email.toLowerCase();
  const isOMGroup      = omGroupEmails.has(currentPerson.m365Email.toLowerCase());
  const isRequester    = order.requesterEmail.toLowerCase() === email;

  // Resolve live LM email: prefer current person record for the requester over stored order value
  const resolvedLMEmail = (() => {
    if (!people?.length) return order.lineManagerEmail.toLowerCase();
    const requesterPerson = people.find(p => p.m365Email.toLowerCase() === order.requesterEmail.toLowerCase());
    return (requesterPerson?.lineManagerEmail || order.lineManagerEmail).toLowerCase();
  })();

  // Live people-list lookups used in stage ribbon and access checks
  const resolvedLMName   = people?.find(p => p.m365Email.toLowerCase() === resolvedLMEmail)?.fullName ?? order.lineManagerName ?? 'awaiting';
  const resolvedCFOName  = order.cfoApproverEmail
    ? (people?.find(p => p.m365Email.toLowerCase() === order.cfoApproverEmail!.toLowerCase())?.fullName ?? order.cfoApproverEmail)
    : 'awaiting';
  const chemApproverName = (() => {
    if (!people?.length) return 'Chemical Approver';
    if (currentPerson.isChemicalApprover) return currentPerson.fullName;
    return people.find(p => p.isChemicalApprover)?.fullName ?? 'Chemical Approver';
  })();
  const omGroupNames     = (() => {
    const members = (people ?? []).filter(p => omGroupEmails.has(p.m365Email.toLowerCase()));
    return members.length > 0 ? members.map(p => p.fullName).join(', ') : 'OM Group';
  })();

  // ── Full edit mode: requester on their own Draft/Rejected, OR OM group member on any non-cancelled order ─
  // isOMEditingLive = OM member editing an order that isn't the requester's own Draft/Rejected flow
  const isOMEditingLive = isOMGroup && !(isRequester && (order.status === OrderStatus.Draft || order.status === OrderStatus.Rejected));
  // Requester can also toggle-edit when the order is parked For Review
  const canToggleEdit = isOMEditingLive || (isRequester && order.status === OrderStatus.ForReview);

  const draftTotalAmount  = draftLineItems.reduce((s, li) => s + li.subtotal, 0);
  const draftSterlingValue =
    draftForm.currency === OrderCurrency.GBP
      ? draftTotalAmount
      : parseFloat(draftForm.sterlingEquivalent) || 0;
  const draftHasChemical  = draftLineItems.some(li => li.isChemical);
  const cs = CURR_SYMBOL[draftForm.currency] ?? '£';
  const fmt   = (v: number) => `${cs}${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const persistDraftData = async (status: OrderStatus, isLocked?: boolean, isSelfApprovedFlag?: boolean) => {
    const totalAmt = draftLineItems.reduce((s, li) => s + li.subtotal, 0);
      const updates: Partial<OrderData> = {
        title: draftForm.title.trim(),
        costAllocation: draftForm.costAllocation,
        projectCode: draftForm.projectCode || '',
        projectName: draftForm.projectName || '',
        supplier: draftForm.supplier.trim(),
        priority: draftForm.priority,
        currency: draftForm.currency,
        totalAmount: totalAmt,
        sterlingEquivalent: draftForm.currency === OrderCurrency.GBP ? totalAmt : (parseFloat(draftForm.sterlingEquivalent) || null),
        requiredByDate: draftForm.requiredByDate || null,
        notes: draftForm.notes.trim(),
        hasChemicalLines: draftLineItems.some(li => li.isChemical),
        fyiViewerEmails: draftFyiViewers,
        status,
      };
      if (isLocked !== undefined) updates.isLocked = isLocked;
      if (isSelfApprovedFlag !== undefined) updates.isSelfApproved = isSelfApprovedFlag;
      if (status !== OrderStatus.Draft) {
        updates.orderDate = new Date().toISOString().split('T')[0];
      }
      await updateOrder(updates);
      // Replace line items: delete existing, save new
      for (const li of lineItems) {
        await connector.deleteOrderLineItem(li.id);
      }
      const newItems: NewLineItemData[] = draftLineItems.map((li, idx) => ({
        orderId: order.spId,
        itemDescription: li.itemDescription,
        sku: li.sku,
        itemLink: li.itemLink,
        quantity: li.quantity,
        unitOfMeasure: li.unitOfMeasure,
        unitPrice: li.unitPrice,
        subtotal: li.subtotal,
        isChemical: li.isChemical,
        coshhRef: li.coshhRef,
        lineOrder: idx + 1,
      }));
      await connector.saveOrderLineItems(order.spId, newItems);
  };

  // OM full-edit save: preserves current order status, just updates fields + line items
  const handleOMFullSave = async (notifyOM = false) => {
    setDraftError(null);
    setDraftActiveAction('save');
    setIsDraftSaving(true);
    setShowNotifyOMConfirm(false);
    try {
      const saveStatus = notifyOM ? OrderStatus.OMApproved : order.status;
      await persistDraftData(saveStatus);
      if (draftAttachments.length) {
        for (const f of draftAttachments) await uploadAttachment(order, f, 'quote');
        setDraftAttachments([]);
      }
      if (notifyOM) {
        const _omEmails = Array.from(omGroupEmails).join(';');
        const _totalAmt = draftLineItems.reduce((s, li) => s + li.subtotal, 0);
        const _sterlingAmt = draftForm.currency === OrderCurrency.GBP ? _totalAmt : parseFloat(draftForm.sterlingEquivalent) || 0;
        await connector.triggerOrderWorkflow({
          triggerType: OrderWorkflowTriggerType.ForReviewUpdatedByRequester,
          orderRef: order.orderRef,
          orderId: order.spId,
          orderTitle: draftForm.title,
          orderAmount: _sterlingAmt,
          recipientEmail: _omEmails,
          recipientName: 'OM Group',
          requesterEmail: currentPerson.m365Email,
          requesterName: currentPerson.fullName,
          templateName: 'for-review-updated-by-requester',
          projectName: draftForm.projectName,
        });
        await addComment({
          orderId: order.spId,
          authorName: currentPerson.fullName,
          authorEmail: currentPerson.m365Email,
          commentText: `Order details updated by requester and OM group notified to continue processing.`,
        });
      }
      draftInitializedRef.current = false;
      setOMEditOpen(false);
      reload();
    } catch (e) {
      setDraftError(e instanceof Error ? e.message : 'Failed to save order.');
    } finally {
      setIsDraftSaving(false);
      setDraftActiveAction(null);
    }
  };

  const renderDraftEditSections = () => (
    <>
        {/* Step 1: Category */}
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">1</span>Where should the cost be allocated?</div>
          <div className="cat-picker">
            {CATEGORY_CARDS.map(c => (
              <button
                key={c.cat}
                className={`cat-card${draftForm.costAllocation === c.cat ? ' selected' : ''}`}
                onClick={() => setDraftField('costAllocation', c.cat)}
                type="button"
              >
                {c.icon}
                <div className="cn">{CAT_NAMES[c.cat]}</div>
                <div className="cd">{c.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Order details */}
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">2</span>Order details</div>
          <div className="ord-grid-2">
            <div className="form-field" style={{ display: draftForm.costAllocation === OrderCostAllocation.ProjectCost ? undefined : 'none' }}>
              <label>Project <span className="req">*</span></label>
              <select
                className="finput"
                value={draftForm.projectCode}
                onChange={e => {
                  const proj = draftProjects.find(p => p.projectCode === e.target.value);
                  setDraftField('projectCode', e.target.value);
                  setDraftField('projectName', proj?.projectName ?? '');
                }}
              >
                <option value="">— Select a project —</option>
                {draftProjects.map(p => (
                  <option key={p.projectCode} value={p.projectCode}>{p.projectCode} — {p.projectName}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Required by <span className="req">*</span></label>
              <DatePickerInput value={draftForm.requiredByDate} onChange={v => setDraftField('requiredByDate', v)} />
            </div>
            <div className="form-field">
              <label>Supplier</label>
              <input
                className="finput"
                placeholder="e.g. RS Components, Dell UK"
                value={draftForm.supplier}
                onChange={e => setDraftField('supplier', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Priority</label>
              <select className="finput" value={draftForm.priority} onChange={e => setDraftField('priority', e.target.value as OrderPriority)}>
                <option value={OrderPriority.Standard}>Standard</option>
                <option value={OrderPriority.Urgent}>Urgent</option>
                <option value={OrderPriority.Low}>Low</option>
              </select>
            </div>
          </div>
          <div className="ord-grid-2" style={{ marginTop: 12 }}>
            <div className="form-field">
              <label>Currency <span className="req">*</span></label>
              <select className="finput" value={draftForm.currency} onChange={e => setDraftField('currency', e.target.value as OrderCurrency)}>
                {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            {draftForm.currency !== OrderCurrency.GBP && (
              <div className="form-field">
                <label>Sterling equivalent <span className="req">*</span></label>
                <input
                  className="finput"
                  type="number"
                  step="0.01"
                  min="0"
                  value={draftForm.sterlingEquivalent}
                  onChange={e => setDraftField('sterlingEquivalent', e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="form-field" style={{ marginTop: 12 }}>
            <label>Order title <span className="req">*</span></label>
            <input
              className="finput"
              placeholder="e.g. Replacement laptop for Simon Mitchell"
              value={draftForm.title}
              onChange={e => setDraftField('title', e.target.value)}
            />
          </div>
          <div className="form-field" style={{ marginTop: 12 }}>
            <label>Notes to approver</label>
            <textarea
              className="fnote"
              placeholder="Why is this needed? Any context for the approver..."
              value={draftForm.notes}
              onChange={e => setDraftField('notes', e.target.value)}
            />
          </div>
        </div>

        {/* Step 3: Line items */}
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">3</span>Line items</div>
          <div className="ord-li-scroll-wrap">
          <table className="li-table">
            <colgroup>
              <col style={{ width: '4%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ textAlign: 'center' }}>#</th>
                <th>Description</th>
                <th>SKU</th>
                <th style={{ textAlign: 'center' }}>Qty</th>
                <th style={{ textAlign: 'center' }}>UOM</th>
                <th style={{ textAlign: 'right' }}>Unit price</th>
                <th style={{ textAlign: 'right' }}>Line total</th>
              </tr>
            </thead>
            <tbody>
              {draftLineItems.map((li, idx) => (
                <Fragment key={li.id}>
                <tr>
                  <td style={{ textAlign: 'center', color: 'var(--text-dim)', fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{idx + 1}</td>
                  <td>
                    <div className="li-desc-row">
                      {draftLineItems.length > 1 && (
                        <button
                          className="li-del"
                          type="button"
                          onClick={() => setConfirmDeleteId(li.id)}
                          title="Remove line"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      )}
                      <input
                        className="li-input"
                        placeholder="Item description (e.g. IPA 99% – item code XYZ)"
                        value={li.itemDescription}
                        onChange={e => updateDraftLine(li.id, { itemDescription: e.target.value })}
                      />
                    </div>
                    <label className="coshh-toggle">
                      <input
                        type="checkbox"
                        checked={li.isChemical}
                        onChange={e => updateDraftLine(li.id, { isChemical: e.target.checked, coshhRef: '' })}
                      />
                      {' '}This line is a chemical (COSHH required)
                    </label>
                    {li.isChemical && (
                      <>
                        {(() => {
                          const term = (coshhSearchTerms[li.id] ?? '').toLowerCase();
                          const filtered = term
                            ? draftCOSHH.filter(c =>
                                c.coshhRef.toLowerCase().includes(term) ||
                                c.chemicalName.toLowerCase().includes(term) ||
                                c.hazardType.toLowerCase().includes(term) ||
                                c.description.toLowerCase().includes(term)
                              )
                            : draftCOSHH;
                          const selected = li.coshhRef && li.coshhRef !== '__request'
                            ? draftCOSHH.find(c => (c.coshhRef || String(c.id)) === li.coshhRef)
                            : null;
                          const isOpen = coshhOpenIds[li.id] ?? false;
                          return (
                            <div className="coshh-search-wrap">
                              <div className="coshh-input-row">
                                <input
                                  ref={el => { coshhInputRefs.current[li.id] = el; }}
                                  className={`coshh-search-input${li.coshhRef === '__request' ? ' missing' : ''}`}
                                  placeholder={selected ? `${selected.chemicalName} - ${selected.hazardType}` : 'Search by chemical name or hazard type…'}
                                  value={coshhSearchTerms[li.id] ?? ''}
                                  onFocus={() => setCoshhOpenIds(p => ({ ...p, [li.id]: true }))}
                                  onBlur={() => setTimeout(() => setCoshhOpenIds(p => ({ ...p, [li.id]: false })), 150)}
                                  onChange={e => {
                                    setCoshhSearchTerms(p => ({ ...p, [li.id]: e.target.value }));
                                    if (e.target.value) updateDraftLine(li.id, { coshhRef: '' });
                                    setCoshhOpenIds(p => ({ ...p, [li.id]: true }));
                                  }}
                                />
                                {li.coshhRef && (
                                  <button className="coshh-clear" type="button" onMouseDown={e => { e.preventDefault(); updateDraftLine(li.id, { coshhRef: '' }); setCoshhSearchTerms(p => ({ ...p, [li.id]: '' })); }}>×</button>
                                )}
                              </div>
                              {li.coshhRef === '__request' && (
                                <div className="coshh-request-note">Not on register — submit a registration request to H&amp;S first.</div>
                              )}
                              {isOpen && (() => { void coshhScrollTick; const r = coshhInputRefs.current[li.id]?.getBoundingClientRect(); const pos = r ? { top: r.bottom, left: r.left, width: Math.max(r.width, 480) } : null; return pos ? (
                                <ul className="coshh-dropdown" style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}>
                                  {filtered.map(c => (
                                    <li key={c.id} onMouseDown={() => { updateDraftLine(li.id, { coshhRef: c.coshhRef || String(c.id) }); setCoshhSearchTerms(p => ({ ...p, [li.id]: '' })); setCoshhOpenIds(p => ({ ...p, [li.id]: false })); }}>
                                      <span className="coshh-dd-ref">{c.chemicalName}</span>
                                      <span className="coshh-dd-hazard">{c.hazardType}</span>
                                    </li>
                                  ))}
                                  {filtered.length === 0 && <li className="coshh-dd-empty">No matches</li>}
                                  <li className="coshh-dd-request" onMouseDown={() => { updateDraftLine(li.id, { coshhRef: '__request' }); setCoshhSearchTerms(p => ({ ...p, [li.id]: '' })); setCoshhOpenIds(p => ({ ...p, [li.id]: false })); window.open('https://evovetech.sharepoint.com/sites/EvoveTechHub/_layouts/15/listforms.aspx?cid=NGQ4MWMxN2EtNDY1My00YjY3LTk0NDItNTI0Mzc0NTY2ZDdm&nav=YzNiZGEzMWYtNjFmOS00ZWJmLTk0NDctOGJlNDhlMTA1NDBk&xsdata=MDV8MDJ8fGJmYWI0NzNkZGE5NjRjODlkNzdjMDhkZWM1NDBmMzQyfDk2MTNkMTQ2ZmE4YjRmZjM5NjY1ZTFmZTY5YzVlYzQ1fDB8MHw2MzkxNjUwNzk4ODMwMTE5NjR8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA5fDF8TDJOb1lYUnpMekU1T2pJeFl6ZGhObVpoTFRnME1qZ3ROREEzT1MxaVlUWTBMVFV3TnprME1HRXpPVE5qTmw5bE1XWmtPVFV6WXkxallXSTNMVFJrTVRrdFlXWTROeTFsTURjMk5qZGpZekV6WVRKQWRXNXhMbWRpYkM1emNHRmpaWE12YldWemMyRm5aWE12TVRjNE1Ea3hNVEU0TmpZek9RPT18NjYwNDM5NjY5MTVmNDNmNjZmMDEwOGRlYzU0MGYzNDJ8YWFiNjRkZmRmZGQ3NGM1MmJjNDc0ODgxMjhkNDQyZjA%3D&sdata=WXBnUHV1WEI4SzZkR0tKM2pkSWk3RkJQWWFNN2lLZ0lFQ3JiME1neGNvND0%3D&ovuser=9613d146-fa8b-4ff3-9665-e1fe69c5ec45%2Cdevops%40coreshare.co.uk', '_blank'); }}>+ Request new COSHH entry via H&amp;S…</li>
                                </ul>
                              ) : null; })()}
                            </div>
                          );
                        })()}
                        {li.coshhRef && li.coshhRef !== '__request' && (() => { const entry = draftCOSHH.find(c => c.coshhRef === li.coshhRef); return entry?.description ? <div className="coshh-description">{entry.description}</div> : null; })()}
                      </>
                    )}
                  </td>
                  <td>
                    <input
                      className="li-input li-sku-input"
                      placeholder="SKU / part number (optional)"
                      value={li.sku}
                      onChange={e => updateDraftLine(li.id, { sku: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="li-input li-qty"
                      type="number"
                      min={1}
                      value={li.quantity}
                      onChange={e => updateDraftLine(li.id, { quantity: parseFloat(e.target.value) || 1 })}
                    />
                  </td>
                  <td>
                    <select
                      className="li-input li-uom"
                      value={li.unitOfMeasure}
                      onChange={e => updateDraftLine(li.id, { unitOfMeasure: e.target.value })}
                    >
                      <option value="">— select —</option>
                      {unitOfMeasureOptions.map(uom => <option key={uom} value={uom}>{uom}</option>)}
                    </select>
                  </td>
                  <td>
                    <input
                      className="li-input li-price"
                      type="number"
                      min={0}
                      step="0.01"
                      value={li.unitPrice}
                      onChange={e => updateDraftLine(li.id, { unitPrice: parseFloat(e.target.value) || 0 })}
                    />
                  </td>
                  <td className="li-total">{fmt(li.subtotal)}</td>
                </tr>
                <tr className="li-link-row">
                  <td />
                  <td colSpan={6}>
                    {looksLikeUrl(li.itemLink) && focusedLinkId !== li.id ? (
                      <div className="li-link-bubble">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                        <a href={normaliseUrl(li.itemLink)} target="_blank" rel="noopener noreferrer" title={li.itemLink}>{li.itemLink.length > 80 ? li.itemLink.slice(0, 77) + '…' : li.itemLink}</a>
                        <button type="button" className="li-link-clear" onClick={() => updateDraftLine(li.id, { itemLink: '' })} title="Remove link">×</button>
                      </div>
                    ) : (
                      <input className="li-input li-link-input" placeholder="Paste product link here (optional)" value={li.itemLink} onFocus={() => setFocusedLinkId(li.id)} onBlur={() => setFocusedLinkId(null)} onChange={e => updateDraftLine(li.id, { itemLink: e.target.value })} onPaste={e => { const t = e.clipboardData.getData('text'); if (looksLikeUrl(t)) { e.preventDefault(); updateDraftLine(li.id, { itemLink: t.trim() }); } }} />
                    )}
                  </td>
                </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
          <div className="li-totals">
            <div><span className="lbl">Total</span><span className="val grand">{fmt(draftTotalAmount)}</span></div>
          </div>
          </div>{/* ord-li-scroll-wrap */}
          <button className="li-add" type="button" onClick={() => setDraftLineItems(prev => [...prev, newDraftLine()])}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add line item
          </button>
          {draftHasChemical && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>⚠</span> This order contains chemical items — COSHH register compliance will be checked at submission.
            </div>
          )}
        </div>

        {/* Step 4: Attachments */}
        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">4</span>Attachments</div>
          {/* Existing uploaded files */}
          {attachments.length > 0 && (
            <div className="attach-list" style={{ marginBottom: 8 }}>
              {attachments.map(att => (
                <div className="attach-row" key={att.serverRelUrl}>
                  <svg className="fi" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span className="atname">{att.name}</span>
                  <span className="atmeta">Quote</span>
                  <button className="atdel" title="Download" onClick={() => downloadAttachment(att)}>↓</button>
                </div>
              ))}
            </div>
          )}
          {/* New files to upload */}
          {draftAttachments.length > 0 && (
            <div className="attach-list" style={{ marginBottom: 8 }}>
              {draftAttachments.map((f, idx) => (
                <div className="attach-row" key={`${f.name}-${idx}`}>
                  <svg className="fi" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span className="atname" style={{ color: 'var(--text-muted)' }}>{f.name}</span>
                  <span className="atmeta" style={{ color: 'var(--accent)' }}>Pending</span>
                  <button className="atdel" title="Remove" onClick={() => setDraftAttachments(prev => prev.filter((_, i) => i !== idx))}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div
            className={`attach-drop${isDragOver ? ' drag-over' : ''}`}
            onClick={() => draftFileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDragEnter={e => { e.preventDefault(); dragCounterRef.current += 1; setIsDragOver(true); }}
            onDragLeave={() => { dragCounterRef.current -= 1; if (dragCounterRef.current === 0) setIsDragOver(false); }}
            onDrop={e => {
              e.preventDefault();
              e.stopPropagation();
              dragCounterRef.current = 0;
              setIsDragOver(false);
              const files = Array.from(e.dataTransfer.files);
              if (files.length) setDraftAttachments(prev => [...prev, ...files]);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            {isDragOver ? 'Drop files here' : 'Drop quote PDFs here or click to browse'}
          </div>
          <input
            ref={draftFileRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg"
            multiple
            style={{ display: 'none' }}
            onChange={e => {
              const files = Array.from(e.target.files ?? []);
              if (files.length) setDraftAttachments(prev => [...prev, ...files]);
              e.target.value = '';
            }}
          />
        </div>

        <div className="ord-form-section">
          <div className="ord-form-title"><span className="ord-step-num">5</span>FYI Viewers <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--text-muted)' }}>(optional)</span></div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>People who should be able to view this order but are not part of the approval chain.</div>
          {draftFyiViewers.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {draftFyiViewers.map(email => {
                const fp = (people ?? []).find(x => x.m365Email.toLowerCase() === email.toLowerCase());
                return (
                  <span key={email} className="fyi-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 11, height: 11, flexShrink: 0 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    {fp ? fp.fullName : email}
                    <button className="fyi-chip-del" type="button" title={`Remove ${email}`} onClick={() => setDraftFyiViewers(v => v.filter(e => e !== email))}>×</button>
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => { setFyiPickerSelected([...draftFyiViewers]); setFyiPickerSearch(''); setShowFYIPickerDraft(true); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
              Select People
            </button>
          </div>
        </div>

        {showFYIPickerDraft && <FYIPickerModal people={people ?? []} selected={fyiPickerSelected} search={fyiPickerSearch} onSearchChange={setFyiPickerSearch} onToggle={email => setFyiPickerSelected(prev => prev.map(e=>e.toLowerCase()).includes(email.toLowerCase()) ? prev.filter(e=>e.toLowerCase()!==email.toLowerCase()) : [...prev,email])} onClose={() => setShowFYIPickerDraft(false)} onConfirm={() => { setDraftFyiViewers([...fyiPickerSelected]); setShowFYIPickerDraft(false); }} isSaving={false} />}
        {/* Delete line item modal */}
        {confirmDeleteId && (() => {
          const target = draftLineItems.find(x => x.id === confirmDeleteId);
          return (
            <div className="li-del-overlay" onClick={() => setConfirmDeleteId(null)}>
              <div className="li-del-modal" onClick={e => e.stopPropagation()}>
                <div className="li-del-modal-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                  </svg>
                </div>
                <div className="li-del-modal-title">Remove line item?</div>
                <div className="li-del-modal-desc">
                  {target?.itemDescription?.trim()
                    ? <><span className="li-del-modal-item">"{target.itemDescription}"</span><br />will be permanently removed from this order.</>
                    : 'This line item will be permanently removed from this order.'
                  }
                </div>
                <div className="li-del-modal-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="li-del-modal-confirm"
                    type="button"
                    onClick={() => {
                      setDraftLineItems(prev => prev.filter(x => x.id !== confirmDeleteId));
                      setConfirmDeleteId(null);
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                    Yes, remove it
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </>
  );

  if ((order.status === OrderStatus.Draft || order.status === OrderStatus.Rejected) && isRequester) {
    const handleSaveDraft = async () => {
      setDraftError(null);
      setDraftActiveAction('save');
      setIsDraftSaving(true);
      try {
        await persistDraftData(OrderStatus.Draft);
        if (draftAttachments.length) {
          for (const f of draftAttachments) await uploadAttachment(order, f, 'quote');
          setDraftAttachments([]);
        }
        draftInitializedRef.current = false; // allow re-init after reload
        reload();
      } catch (e) {
        setDraftError(e instanceof Error ? e.message : 'Failed to save draft.');
      } finally {
        setIsDraftSaving(false);
        setDraftActiveAction(null);
      }
    };

    const handleSubmitDraft = async () => {
      const err = validateDraft();
      if (err) { setDraftError(err); return; }
      setDraftError(null);
      setDraftActiveAction('submit');
      setIsDraftSaving(true);
      try {
        const limit = currentPerson.purchaseApprovalLimit ?? ORDER_SELF_APPROVAL_LIMIT;
        const effectiveSelfLimit = Math.max(ORDER_SELF_APPROVAL_LIMIT, limit);
        // Chemical orders always go to Chemical Approver first, then LM if above limit
        const isSelfApproved = !draftHasChemical && draftSterlingValue <= effectiveSelfLimit;
        const newStatus = isSelfApproved
          ? OrderStatus.OMApproved
          : draftHasChemical
            ? OrderStatus.PendingChemApproval
            : OrderStatus.Requested;
        await persistDraftData(newStatus, isSelfApproved ? true : undefined, isSelfApproved ? true : undefined);
        if (draftAttachments.length) {
          for (const f of draftAttachments) await uploadAttachment(order, f, 'quote');
          setDraftAttachments([]);
        }
        if (isSelfApproved) {
          await addApproval({
            orderId: order.spId,
            approvalStage: ApprovalStage.SelfApproval,
            approverName: currentPerson.fullName,
            approverEmail: currentPerson.m365Email,
            decision: ApprovalDecision.Approved,
            note: 'Self-approved (order within self-approval limit)',
          });
        }
        await addComment({
          orderId: order.spId,
          authorName: currentPerson.fullName,
          authorEmail: currentPerson.m365Email,
          commentText: isSelfApproved
            ? 'Order self-approved and sent to OM for processing.'
            : draftHasChemical
              ? (order.status === OrderStatus.Rejected ? 'Order resubmitted for chemical approval after rejection.' : 'Order submitted for chemical approval.')
              : (order.status === OrderStatus.Rejected ? 'Order resubmitted for approval after rejection.' : 'Order submitted for approval.'),
        });
        const _draftAmount = order.sterlingEquivalent ?? order.totalAmount;
        const _draftBase = { orderRef: order.orderRef, orderId: order.spId, orderTitle: order.title, orderAmount: _draftAmount, requesterEmail: currentPerson.m365Email, requesterName: currentPerson.fullName, projectName: draftForm.projectName };
        connector.triggerOrderWorkflow({ ..._draftBase, triggerType: OrderWorkflowTriggerType.OrderSubmitted, recipientEmail: currentPerson.m365Email, recipientName: currentPerson.fullName, templateName: 'order-submitted' }).catch(() => {});
        if (isSelfApproved) {
          const _omEmails = Array.from(omGroupEmails).join(';');
          connector.triggerOrderWorkflow({ ..._draftBase, triggerType: OrderWorkflowTriggerType.OrderSelfApprovedToOM, recipientEmail: _omEmails, recipientName: 'OM Group', templateName: 'order-self-approved-to-om' }).catch(() => {});
        } else if (draftHasChemical) {
          const _chem = (people ?? []).find(p => p.isChemicalApprover);
          if (_chem) connector.triggerOrderWorkflow({ ..._draftBase, triggerType: OrderWorkflowTriggerType.ChemicalApprovalRequest, recipientEmail: _chem.m365Email, recipientName: _chem.fullName, templateName: 'chemical-approval-request' }).catch(() => {});
        } else {
          connector.triggerOrderWorkflow({ ..._draftBase, triggerType: OrderWorkflowTriggerType.LMApprovalRequest, recipientEmail: order.lineManagerEmail, recipientName: order.lineManagerName, templateName: 'lm-approval-request' }).catch(() => {});
        }
        const _submitSub = isSelfApproved
          ? 'Self-approved and sent to OM for processing.'
          : draftHasChemical
          ? 'Sent to Chemical Approver for review.'
          : 'Sent to your Line Manager for approval.';
        showSuccessToast({ title: 'Order Submitted!', sub: _submitSub, icon: 'submit' }, () => onSubmitted ? onSubmitted() : onBack());
      } catch (e) {
        setDraftError(e instanceof Error ? e.message : 'Failed to submit order.');
      } finally {
        setIsDraftSaving(false);
        setDraftActiveAction(null);
      }
    };

    return (
      <>
      <div className="ord-view-content">
        {/* Header */}
        <div className="ord-detail-hdr">
          <div>
            <div className="ord-detail-title">{order.orderRef} · {draftForm.title || 'Draft Order'}</div>
            <div className="ord-detail-sub">
              {order.status === OrderStatus.Rejected
                ? 'Rejected — edit and resubmit for approval'
                : 'Draft — editing in progress'
              }
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <OrderStatusPill status={order.status} />
            <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginLeft: 4 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 14, height: 14 }}>
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Info box (requester only — shows submission flow) */}
        <div className="validation-box">
          <div className="vh">What happens when you submit</div>
          <ul>
            <li><strong>Self-approval</strong> — orders within your approval limit are automatically approved and sent directly to the Order Management (OM) team.</li>
            <li><strong>Line Manager</strong> — orders exceeding your approval limit are routed to your line manager for approval.</li>
            <li><strong>C-Level Approval</strong> — orders up to £10,000 require approval from a C-level executive.</li>
            <li><strong>CEO &amp; CFO Approval</strong> — orders over £10,000 require approval from both the CEO and CFO.</li>
            <li><strong>Chemical Approval</strong> — all chemical-related orders are first routed to the designated chemical approver.</li>
          </ul>
        </div>

        {renderDraftEditSections()}

            {/* Comments & audit trail (read-only in edit mode) */}
        {order.status === OrderStatus.Rejected && unifiedTimeline.length > 0 && (
          <div className="comment-thread">
            <div className="ct-hdr">
              Comments &amp; Audit Trail
              <span className="ct-count">{unifiedTimeline.length} entr{unifiedTimeline.length !== 1 ? 'ies' : 'y'}</span>
            </div>
            <div className="ct-list">
              {unifiedTimeline.map((entry, i) => {
                if (entry.kind === 'system') {
                  return (
                    <div key={`sys-${i}`} className="ct-item ct-item-system">
                      <div className="ct-hdr-row">
                        <span className="ct-role ct-sys">System</span>
                        <span className="ct-ts">{fmtDateTime(entry.ts)}</span>
                      </div>
                      <div className="ct-body ct-body-muted">{entry.text}</div>
                    </div>
                  );
                }
                if (entry.kind === 'approval') {
                  const log = entry.log;
                  const approved = log.decision === ApprovalDecision.Approved;
                  return (
                    <div key={log.id} className={`ct-item${approved ? ' ct-item-approved' : ' ct-item-rejected'}`}>
                      <div className="ct-hdr-row">
                        <span className={`ct-role ${approved ? 'ct-app' : 'ct-rej'}`}>
                          {log.approverName}
                          {log.onBehalfOf && <span style={{ fontWeight: 400 }}> · on behalf of {log.onBehalfOf}</span>}
                        </span>
                        <span className="ct-ts">{fmtDateTime(log.approvalDate)}</span>
                      </div>
                      <div className="ct-body">
                        <span className={approved ? 'ord-log-approved' : 'ord-log-rejected'}>{log.decision}</span>
                        <span style={{ color: 'var(--text-dim)', fontSize: 12 }}> · {log.approvalStage}</span>
                        {log.note && <span style={{ fontStyle: 'italic' }}> — {log.note}</span>}
                      </div>
                    </div>
                  );
                }
                const c = entry.comment;
                const isReq = c.authorEmail.toLowerCase() === order.requesterEmail.toLowerCase();
                const isSys = c.authorName === 'System' || c.authorEmail === 'system';
                const roleClass = isSys ? 'ct-sys' : isReq ? 'ct-req' : 'ct-app';
                return (
                  <div key={c.id} className={`ct-item${isSys ? ' ct-item-system' : ''}`}>
                    <div className="ct-hdr-row">
                      <span className={`ct-role ${roleClass}`}>{c.authorName}</span>
                      <span className="ct-ts">{fmtDateTime(c.commentDate)}</span>
                    </div>
                    <div className={`ct-body${isSys ? ' ct-body-muted' : ''}`}>{c.commentText}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Error */}
        {draftError && <div className="err-banner" style={{ marginBottom: 16 }}>{draftError}</div>}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
          <button className="btn btn-ghost" type="button" onClick={onBack}>Cancel</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Requester editing their own Draft / Rejected order */}
            <button
              className="btn btn-ghost"
              type="button"
              onClick={handleSaveDraft}
              disabled={isDraftSaving || isSaving}
            >
              {isDraftSaving && draftActiveAction === 'save'
                ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
                : 'Save Draft'
              }
            </button>
            <button
              className="btn btn-success"
              type="button"
              onClick={() => { submitConfirmFnRef.current = handleSubmitDraft; setShowSubmitConfirm(true); }}
              disabled={isDraftSaving || isSaving}
            >
              {isDraftSaving && draftActiveAction === 'submit' ? (
                <><div className="spinner" style={{ width: 14, height: 14 }} /> Submitting…</>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 14, height: 14 }}>
                    <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" />
                  </svg>
                  Submit for Approval
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Submit confirmation modal (draft mode) ─────────────────────────── */}
      {showSubmitConfirm && (
        <div className="li-del-overlay" onClick={() => setShowSubmitConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon" style={{ background: 'rgba(34,197,94,0.1)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--success,#22c55e)" strokeWidth="1.8" style={{ width: 24, height: 24 }}>
                <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" />
              </svg>
            </div>
            <div className="li-del-modal-title">Submit for Approval?</div>
            <div className="li-del-modal-desc">
              Once submitted, your order will be sent for approval. You won't be able to edit it until a decision is made.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowSubmitConfirm(false)}>Cancel</button>
              <button
                className="li-del-modal-confirm"
                style={{ background: 'var(--success,#22c55e)' }}
                disabled={isDraftSaving}
                onClick={() => { setShowSubmitConfirm(false); submitConfirmFnRef.current?.(); }}
              >
                {isDraftSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Submitting…</> : 'Yes, Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }

  // ── View mode (non-draft) ─────────────────────────────────────────────────

  const isChemApprover = currentPerson.isChemicalApprover;
  const isLM           = resolvedLMEmail === email;
  const isCFO          = currentPerson.isCFO;

  const sterlingVal       = order.sterlingEquivalent ?? order.totalAmount;

  // Computed dynamically on every visit from the live people list + approval log.
  const _financialApproverResult = (
    (order.status === OrderStatus.Requested || order.status === OrderStatus.PendingEscalatedApproval) && people?.length
  )
    ? findCurrentFinancialApprover(order.lineManagerEmail, sterlingVal, people, approvalLog, ORDER_SELF_APPROVAL_LIMIT)
    : null;
  const currentFinancialApprover = _financialApproverResult === 'no-approver' ? null : _financialApproverResult;
  const isCurrentFinancialApprover = currentFinancialApprover?.email.toLowerCase().trim() === email;
  const financialChainBlocked = _financialApproverResult === 'no-approver';

  const isOBOCEOPerson = (people ?? []).some(p => p.m365Email === currentPerson.m365Email && p.canApproveOnBehalfOfCEO);

  const isFYIOnlyViewer =
    !isRequester && !isOMGroup && !isLM && !isCFO && !isChemApprover && !isCurrentFinancialApprover && !isOBOCEOPerson &&
    order.fyiViewerEmails.some(e => e.toLowerCase() === email);

  const hasOrderAccess = isRequester || isOMGroup || isLM || isCFO || isChemApprover || isCurrentFinancialApprover || isOBOCEOPerson || isFYIOnlyViewer;


  const requiresCEOandCFO = sterlingVal >= ORDER_CEO_CFO_THRESHOLD;

  const canFinancialApprove = isCurrentFinancialApprover;
  const isCEOPending        = order.status === OrderStatus.PendingCEOApproval;
  const isCFOPending   = order.status === OrderStatus.PendingCFOApproval;
  const isChemPending  = order.status === OrderStatus.PendingChemApproval || order.status === OrderStatus.LMApproved;
  const canCEOOBOApprove =
    isOBOCEOPerson && isCEOPending && !order.approvedOnBehalfOf && !financialChainBlocked;

  const canCFOApprove =
    isCFO && isCFOPending && requiresCEOandCFO && !order.cfoApproverEmail && !financialChainBlocked;

  const canChemApprove =
    isChemApprover &&
    order.hasChemicalLines &&
    isChemPending &&
    !financialChainBlocked;

  const canOMProcess =
    isOMGroup &&
    (order.status === OrderStatus.OMApproved || order.status === OrderStatus.ChemicalApproved) &&
    !financialChainBlocked;

  const isTerminalStatus =
    order.status === OrderStatus.Delivered ||
    order.status === OrderStatus.Cancelled ||
    order.status === OrderStatus.Rejected;

  const canUploadAttachment = hasOrderAccess && !isTerminalStatus && !isFYIOnlyViewer &&
    (!order.isLocked || isOMGroup) && (
    isRequester || isOMGroup || isChemApprover || isCFO || canFinancialApprove
  );

  // OM group can edit fields on any order at any stage (per business spec).
  // Individual action buttons inside the panel (Cancel, Mark Delivered, etc.) carry their own status guards.
  const canEditOMFields = isOMGroup;

  const canApprove =
    canFinancialApprove || canCEOOBOApprove || canCFOApprove || canChemApprove || canOMProcess;

  const approvalStageLabel =
    canFinancialApprove && currentFinancialApprover?.isDirectLM ? 'Line Manager Approval' :
    canFinancialApprove                                          ? 'Extended Approval' :
    canCEOOBOApprove                                             ? 'CEO Approval — On Behalf Of' :
    canCFOApprove                                                ? 'CFO Approval' :
    canChemApprove                                               ? 'Chemical Approval' :
    canOMProcess                                                 ? 'OM Group — Mark as Ordered' : '';

  type SgClass = 'done' | 'current' | 'reject' | '';
  const rejected  = order.status === OrderStatus.Rejected;
  const cancelled = order.status === OrderStatus.Cancelled;
  const isBad     = rejected || cancelled;

  // ── Dynamic stage ribbon builder ─────────────────────────────────────────
  const stageLabels = (() => {
    const stages: { cls: SgClass; lbl: string; by: string }[] = [];

    // Stage 1: Submit
    const submitted = order.status !== OrderStatus.Draft;
    stages.push({
      cls: submitted ? 'done' : 'current',
      lbl: 'Submit',
      by: order.requesterName,
    });

    // Stage 2: Chemical Approval (if chemical order — comes before LM in the new flow)
    // After a rejection + resubmission, previous approvals are stale. Find the most recent
    // rejection date so we can ignore any log entries that predate it.
    const rejectionDates = approvalLog
      .filter(l => l.decision === ApprovalDecision.Rejected)
      .map(l => l.approvalDate)
      .sort();
    const lastRejectionDate = rejectionDates.length ? rejectionDates[rejectionDates.length - 1] : null;

    if (order.hasChemicalLines) {
      const chemApprovalDone = approvalLog.some(
        l => l.approvalStage === ApprovalStage.Chemical &&
             l.decision === ApprovalDecision.Approved &&
             (!lastRejectionDate || l.approvalDate > lastRejectionDate)
      );
      // Past-chem statuses: anything after PendingChemApproval
      const chemPostStatuses: OrderStatus[] = [
        OrderStatus.Requested, OrderStatus.PendingEscalatedApproval,
        OrderStatus.PendingCEOApproval, OrderStatus.PendingCFOApproval,
        OrderStatus.ChemicalApproved, OrderStatus.OMApproved,
        OrderStatus.Ordered, OrderStatus.PartialDelivery, OrderStatus.Delivered,
      ];
      const chemDone = chemApprovalDone || chemPostStatuses.includes(order.status);
      const chemCls: SgClass = isBad ? 'reject' : chemDone ? 'done' : isChemPending ? 'current' : '';
      stages.push({ cls: chemCls, lbl: 'Chemical', by: chemApproverName });
    }

    // Detect the chemical-direct path (≤ self-approval limit — no LM needed)
    const isChemDirectPath =
      order.hasChemicalLines &&
      sterlingVal <= ORDER_SELF_APPROVAL_LIMIT &&
      !approvalLog.some(l => l.approvalStage === ApprovalStage.LineManager);

    // Stage 3: Self-Approval OR Line Manager (skipped for chemical-direct path)
    const wasSelfApproved =
      approvalLog.some(l => l.approvalStage === ApprovalStage.SelfApproval) ||
      (!order.hasChemicalLines &&
       sterlingVal <= ORDER_SELF_APPROVAL_LIMIT &&
       order.status === OrderStatus.OMApproved &&
       !approvalLog.some(l => l.approvalStage === ApprovalStage.LineManager));

    if (isChemDirectPath && !wasSelfApproved) {
      // No LM stage — chemical routed directly to Chemical Approver
    } else if (wasSelfApproved) {
      stages.push({ cls: 'done', lbl: 'Self-Approved', by: order.requesterName });
    } else {
      // Financial approval chain — one bubble per log entry (LM + any escalation),
      // plus a live pending bubble computed dynamically from the hierarchy.
      const financialLogs = approvalLog.filter(l =>
        (l.approvalStage === ApprovalStage.LineManager ||
         l.approvalStage === ApprovalStage.EscalatedApproval) &&
        (!lastRejectionDate || l.approvalDate > lastRejectionDate)
      );
      for (const log of financialLogs) {
        stages.push({
          cls: 'done',
          lbl: log.approvalStage === ApprovalStage.LineManager ? 'Line Manager' : 'Extended Approval',
          by: log.approverName,
        });
      }
      if (currentFinancialApprover) {
        stages.push({
          cls: isBad ? 'reject' : 'current',
          lbl: currentFinancialApprover.isDirectLM ? 'Line Manager' : 'Extended Approval',
          by: currentFinancialApprover.name,
        });
      } else if (order.status === OrderStatus.PendingEscalatedApproval) {
        stages.push({ cls: 'current', lbl: 'Extended Approval', by: 'awaiting assignment' });
      } else if (financialLogs.length === 0 && [
        OrderStatus.LMApproved, OrderStatus.PendingCEOApproval, OrderStatus.PendingCFOApproval,
        OrderStatus.OMApproved, OrderStatus.Ordered, OrderStatus.PartialDelivery, OrderStatus.Delivered,
      ].includes(order.status)) {
        stages.push({ cls: 'done', lbl: 'Line Manager', by: resolvedLMName });
      }

      // CEO + CFO (≥£10k)
      if (requiresCEOandCFO) {
        const ceoDone = !!order.approvedOnBehalfOf;
        const ceoPostStatuses = [
          OrderStatus.PendingCFOApproval, OrderStatus.ChemicalApproved, OrderStatus.OMApproved,
          OrderStatus.Ordered, OrderStatus.PartialDelivery, OrderStatus.Delivered,
        ];
        const ceoCls: SgClass = isBad ? 'reject' : (ceoDone || ceoPostStatuses.includes(order.status)) ? 'done' :
          isCEOPending ? 'current' : '';
        stages.push({ cls: ceoCls, lbl: 'CEO Sign-off', by: order.approvedOnBehalfOf ? `via ${order.approvedOnBehalfOf}` : (isCEOPending && isOBOCEOPerson ? currentPerson.fullName : 'awaiting') });

        const cfoDone = !!order.cfoApproverEmail;
        const cfoPostStatuses = [
          OrderStatus.ChemicalApproved, OrderStatus.OMApproved,
          OrderStatus.Ordered, OrderStatus.PartialDelivery, OrderStatus.Delivered,
        ];
        const cfoCls: SgClass = isBad ? 'reject' : (cfoDone || cfoPostStatuses.includes(order.status)) ? 'done' :
          isCFOPending ? 'current' : '';
        stages.push({ cls: cfoCls, lbl: 'CFO', by: resolvedCFOName });
      }
    }

    // OM Group
    const omPostStatuses = [OrderStatus.Ordered, OrderStatus.PartialDelivery, OrderStatus.Delivered];
    const omDone    = omPostStatuses.includes(order.status);
    const omCurrent = order.status === OrderStatus.OMApproved || order.status === OrderStatus.ChemicalApproved;
    stages.push({ cls: omDone ? 'done' : omCurrent ? 'current' : '', lbl: 'OM Group', by: omGroupNames });

    // Ordered / Delivered
    const deliveryCls: SgClass =
      order.status === OrderStatus.Delivered ? 'done' :
      [OrderStatus.Ordered, OrderStatus.PartialDelivery].includes(order.status) ? 'current' : '';
    const deliveryLbl =
      order.status === OrderStatus.Delivered    ? 'Delivered' :
      order.status === OrderStatus.PartialDelivery ? 'Partial' : 'Ordered';
    stages.push({
      cls: deliveryCls,
      lbl: deliveryLbl,
      by: order.poNumber ?? (order.expectedDeliveryDate ? fmtDate(order.expectedDeliveryDate) : '—'),
    });

    return stages;
  })();

  const viewCs = CURR_SYMBOL[order.currency] ?? '£';
  const subtotal  = lineItems.reduce((s, li) => s + li.subtotal, 0);

  const showSuccessToast = (config: NonNullable<typeof successToast>, callback: () => void) => {
    if (successToastTimerRef.current) clearTimeout(successToastTimerRef.current);
    successToastCallbackRef.current = callback;
    setSuccessToast(config);
    successToastTimerRef.current = setTimeout(() => {
      setSuccessToast(null);
      successToastCallbackRef.current?.();
      successToastCallbackRef.current = null;
    }, 4800);
  };

  const dismissSuccessToast = () => {
    if (successToastTimerRef.current) clearTimeout(successToastTimerRef.current);
    setSuccessToast(null);
    successToastCallbackRef.current?.();
    successToastCallbackRef.current = null;
  };

  const handleApprove = async () => {
    let stage: ApprovalStage;
    let nextStatus: OrderStatus = order.status;
    const orderUpdates: Partial<OrderData> = { isLocked: true };

    if (canFinancialApprove && currentFinancialApprover) {
      stage = currentFinancialApprover.isDirectLM ? ApprovalStage.LineManager : ApprovalStage.EscalatedApproval;
      const simulatedLog = [...approvalLog, { approvalStage: stage, approverEmail: currentPerson.m365Email, decision: ApprovalDecision.Approved }];
      const nextApprover = people?.length
        ? findCurrentFinancialApprover(order.lineManagerEmail, sterlingVal, people, simulatedLog, ORDER_SELF_APPROVAL_LIMIT)
        : null;
      if (nextApprover === 'no-approver') {
        orderUpdates.escalatedApproverEmail = '';
        orderUpdates.escalatedApproverName  = '';
        nextStatus = OrderStatus.PendingCEOApproval;
      } else if (nextApprover) {
        orderUpdates.escalatedApproverEmail = nextApprover.email;
        orderUpdates.escalatedApproverName = nextApprover.name;
        nextStatus = OrderStatus.Requested;
      } else {
        if (!currentFinancialApprover.isDirectLM) {
          orderUpdates.escalatedApproverEmail = currentPerson.m365Email;
          orderUpdates.escalatedApproverName = currentPerson.fullName;
        } else {
          orderUpdates.escalatedApproverEmail = '';
          orderUpdates.escalatedApproverName = '';
        }
        if (requiresCEOandCFO) {
          nextStatus = OrderStatus.PendingCEOApproval;
        } else {
          nextStatus = OrderStatus.OMApproved;
        }
      }
    } else if (canCEOOBOApprove) {
      stage = ApprovalStage.CEOOnBehalfOf;
      orderUpdates.approvedOnBehalfOf = currentPerson.fullName;
      nextStatus = requiresCEOandCFO ? OrderStatus.PendingCFOApproval : OrderStatus.OMApproved;
    } else if (canCFOApprove) {
      stage = ApprovalStage.CFO;
      orderUpdates.cfoApproverEmail = email;
      nextStatus = OrderStatus.OMApproved;
    } else if (canChemApprove) {
      stage = ApprovalStage.Chemical;
      // After chemical approval, route to LM if above self-approval limit, otherwise straight to OM
      const requester = (people ?? []).find(p => p.m365Email.toLowerCase() === order.requesterEmail.toLowerCase());
      const reqLimit = requester?.purchaseApprovalLimit ?? ORDER_SELF_APPROVAL_LIMIT;
      const effectiveLimit = Math.max(ORDER_SELF_APPROVAL_LIMIT, reqLimit);
      nextStatus = sterlingVal > effectiveLimit ? OrderStatus.Requested : OrderStatus.ChemicalApproved;
    } else if (canOMProcess) {
      stage = ApprovalStage.OM;
      nextStatus = OrderStatus.Ordered;
    } else {
      return;
    }

    orderUpdates.status = nextStatus;

    let ceoAttachmentRef: string | undefined;
    if (canCEOOBOApprove && ceoEmailFile) {
      const att = await uploadAttachment(order, ceoEmailFile, 'ceo-approval');
      ceoAttachmentRef = att.name;
    }

    await addApproval({
      orderId: order.spId,
      approvalStage: stage,
      approverName: currentPerson.fullName,
      approverEmail: currentPerson.m365Email,
      decision: ApprovalDecision.Approved,
      note: approvalNote,
      onBehalfOf: undefined,
      attachmentRef: ceoAttachmentRef,
    });
    await updateOrder(orderUpdates);

    // Fire order workflow trigger (fire-and-forget — never block the UI on this)
    (() => {
      const _sv = order.sterlingEquivalent ?? order.totalAmount;
      const _base = { orderRef: order.orderRef, orderId: order.spId, orderTitle: order.title, orderAmount: _sv, requesterEmail: order.requesterEmail, requesterName: order.requesterName, projectName: order.projectName };
      const _omEmails = Array.from(omGroupEmails).join(';');
      const _chemEmails = (people ?? []).filter(p => p.isChemicalApprover).map(p => p.m365Email).join(';');
      const _cfoEmails = (people ?? []).filter(p => p.isCFO).map(p => p.m365Email).join(';');
      const _ceoOBOEmails = (people ?? []).filter(p => p.canApproveOnBehalfOfCEO).map(p => p.m365Email).join(';');
      if (canOMProcess) {
        connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.OrderPlaced, recipientEmail: order.requesterEmail, recipientName: order.requesterName, templateName: 'order-placed' }).catch(() => {});
      } else if (canChemApprove) {
        if (nextStatus === OrderStatus.Requested) {
          connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.LMApprovalRequest, recipientEmail: order.lineManagerEmail, recipientName: order.lineManagerName, templateName: 'lm-approval-request' }).catch(() => {});
        } else if (_omEmails) {
          connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.OrderApprovedToOM, recipientEmail: _omEmails, recipientName: 'OM Group', templateName: 'order-approved-to-om' }).catch(() => {});
        }
      } else if (canCFOApprove) {
        if (order.hasChemicalLines && _chemEmails) {
          connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.ChemicalApprovalRequest, recipientEmail: _chemEmails, recipientName: 'Chemical Approvers', templateName: 'chemical-approval-request' }).catch(() => {});
        } else if (_omEmails) {
          connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.OrderApprovedToOM, recipientEmail: _omEmails, recipientName: 'OM Group', templateName: 'order-approved-to-om' }).catch(() => {});
        }
      } else if (canCEOOBOApprove) {
        if (requiresCEOandCFO) {
          if (_cfoEmails) connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.CFOApprovalRequest, recipientEmail: _cfoEmails, recipientName: 'CFO', templateName: 'cfo-approval-request' }).catch(() => {});
        } else {
          if (_omEmails) connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.OrderApprovedToOM, recipientEmail: _omEmails, recipientName: 'OM Group', templateName: 'order-approved-to-om' }).catch(() => {});
        }
      } else if (canFinancialApprove) {
        if (nextStatus === OrderStatus.Requested) {
          const _esc = orderUpdates.escalatedApproverEmail;
          const _escName = orderUpdates.escalatedApproverName ?? '';
          if (_esc) connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.EscalatedApprovalRequest, recipientEmail: _esc, recipientName: _escName, templateName: 'escalated-approval-request' }).catch(() => {});
        } else if (nextStatus === OrderStatus.PendingCEOApproval) {
          if (_ceoOBOEmails) connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.CEOApprovalRequest, recipientEmail: _ceoOBOEmails, recipientName: 'CEO Approvers', templateName: 'ceo-approval-request' }).catch(() => {});
        } else if (nextStatus === OrderStatus.OMApproved && _omEmails) {
          connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.OrderApprovedToOM, recipientEmail: _omEmails, recipientName: 'OM Group', templateName: 'order-approved-to-om' }).catch(() => {});
        }
      }
    })();

    setApprovalNote('');
    setCeoEmailFile(null);
    const _approveTitle = canOMProcess ? 'Order Placed!' : 'Approved!';
    const _approveSub = canOMProcess ? 'Marked as ordered and the requester has been notified.' : 'Forwarded to the next approval stage.';
    showSuccessToast({ title: _approveTitle, sub: _approveSub, icon: 'check' }, () => { onOrderChanged?.(); onBack(); });
  };

  const handleReject = async () => {
    if (!approvalNote.trim()) return;
    let stage: ApprovalStage;
    if (canFinancialApprove)   stage = currentFinancialApprover?.isDirectLM ? ApprovalStage.LineManager : ApprovalStage.EscalatedApproval;
    else if (canCEOOBOApprove) stage = ApprovalStage.CEOOnBehalfOf;
    else if (canCFOApprove)    stage = ApprovalStage.CFO;
    else if (canChemApprove)   stage = ApprovalStage.Chemical;
    else                       stage = ApprovalStage.OM;

    await addApproval({
      orderId: order.spId,
      approvalStage: stage,
      approverName: currentPerson.fullName,
      approverEmail: currentPerson.m365Email,
      decision: ApprovalDecision.Rejected,
      note: approvalNote,
    });
    await updateOrder({ status: OrderStatus.Rejected });
    connector.triggerOrderWorkflow({
      triggerType: OrderWorkflowTriggerType.OrderRejected,
      orderRef: order.orderRef,
      orderId: order.spId,
      orderTitle: order.title,
      orderAmount: order.sterlingEquivalent ?? order.totalAmount,
      recipientEmail: order.requesterEmail,
      recipientName: order.requesterName,
      requesterEmail: order.requesterEmail,
      requesterName: order.requesterName,
      templateName: 'order-rejected',
      projectName: order.projectName,
    }).catch(() => {});
    setApprovalNote('');
    showSuccessToast({ title: 'Order Rejected', sub: 'The requester has been notified with your rejection note.', icon: 'reject' }, () => { onOrderChanged?.(); onBack(); });
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    await addComment({
      orderId: order.spId,
      authorName: currentPerson.fullName,
      authorEmail: currentPerson.m365Email,
      commentText: commentText.trim(),
    });
    setCommentText('');

    // Trigger ADD_COMMENT workflow notification
    const _sv = order.sterlingEquivalent ?? order.totalAmount;
    const _base = {
      orderRef: order.orderRef,
      orderId: order.spId,
      orderTitle: order.title,
      orderAmount: _sv,
      requesterEmail: order.requesterEmail,
      requesterName: order.requesterName,
      projectName: order.projectName,
      note: commentText.trim(),
      comment: commentText.trim(),
    };

    let recipientEmail: string;
    let recipientName: string;

    if (isRequester) {
      // Requester commented — notify the current pending approver
      if (order.status === OrderStatus.PendingEscalatedApproval && order.escalatedApproverEmail) {
        recipientEmail = order.escalatedApproverEmail;
        recipientName  = order.escalatedApproverName || order.escalatedApproverEmail;
      } else if (order.status === OrderStatus.PendingChemApproval) {
        const _chem = (people ?? []).find(p => p.isChemicalApprover);
        recipientEmail = _chem?.m365Email ?? '';
        recipientName  = _chem?.fullName ?? 'Chemical Approver';
      } else if (order.status === OrderStatus.PendingCEOApproval || order.status === OrderStatus.PendingCFOApproval) {
        recipientEmail = order.cfoApproverEmail || resolvedLMEmail;
        recipientName  = people?.find(p => p.m365Email.toLowerCase() === recipientEmail.toLowerCase())?.fullName ?? recipientEmail;
      } else if (order.status === OrderStatus.OMApproved) {
        const _omEmails = Array.from(omGroupEmails).join(';');
        recipientEmail = _omEmails;
        recipientName  = 'OM Group';
      } else {
        recipientEmail = resolvedLMEmail;
        recipientName  = resolvedLMName;
      }
    } else {
      // Approver commented — notify the requester
      recipientEmail = order.requesterEmail;
      recipientName  = order.requesterName;
    }

    if (recipientEmail) {
      connector.triggerOrderWorkflow({
        ..._base,
        triggerType: OrderWorkflowTriggerType.AddComment,
        recipientEmail,
        recipientName,
        templateName: 'add-comment',
      }).catch(() => {});
    }
  };

  const handleOMSave = async () => {
    const updates: Record<string, unknown> = {};
    if (omFields.poNumber)             updates.poNumber = omFields.poNumber;
    if (omFields.costCentreName)       updates.costCentreName = omFields.costCentreName;
    if (omFields.expectedDeliveryDate) updates.expectedDeliveryDate = omFields.expectedDeliveryDate;
    if (omFields.actualDeliveryDate)   updates.actualDeliveryDate = omFields.actualDeliveryDate;
    if (omFields.sterlingEquivalent)   updates.sterlingEquivalent = parseFloat(omFields.sterlingEquivalent);
    await updateOrder(updates as Parameters<typeof updateOrder>[0]);
    setShowOMPanel(false);
  };

  const handleAddFYI = async (emails: string[]) => {
    await updateOrder({ fyiViewerEmails: emails });
    setShowFYIPickerLive(false);
    const _sv = order.sterlingEquivalent ?? order.totalAmount;
    const _base = { orderRef: order.orderRef, orderId: order.spId, orderTitle: order.title, orderAmount: _sv, requesterEmail: order.requesterEmail, requesterName: order.requesterName, projectName: order.projectName };
    const newEmails = emails.filter(e => !order.fyiViewerEmails.map(x => x.toLowerCase()).includes(e.toLowerCase()));
    newEmails.forEach(email => {
      const person = (people ?? []).find(p => p.m365Email.toLowerCase() === email.toLowerCase());
      connector.triggerOrderWorkflow({ ..._base, triggerType: OrderWorkflowTriggerType.FYIViewerInvite, recipientEmail: email, recipientName: person?.fullName ?? email, templateName: 'fyi-viewer-invite' }).catch(() => {});
    });
  };

  const handleDeleteFYI = async () => {
    if (!confirmDeleteFYIEmail) return;
    const updated = order.fyiViewerEmails.filter(e => e.toLowerCase() !== confirmDeleteFYIEmail.toLowerCase());
    await updateOrder({ fyiViewerEmails: [...updated] });
    setConfirmDeleteFYIEmail(null);
  };

  const handleCancelOrder = async () => {
    await updateOrder({ status: OrderStatus.Cancelled });
    await addComment({
      orderId: order.spId,
      authorName: 'System',
      authorEmail: 'system',
      commentText: `Order cancelled by ${currentPerson.fullName}.`,
    });
    setShowCancelConfirm(false);
    showSuccessToast({ title: 'Order Cancelled', sub: 'The record has been retained for audit purposes.', icon: 'cancel' }, () => onOrderChanged?.());
  };

  const handleMarkForReview = async () => {
    await updateOrder({ status: OrderStatus.ForReview });
    await addComment({
      orderId: order.spId,
      authorName: 'System',
      authorEmail: 'system',
      commentText: `Order parked as "For Review" by ${currentPerson.fullName}. No action until cleared.`,
    });
    setShowForReviewConfirm(false);
    showSuccessToast({ title: 'Parked for Review', sub: 'No further action until the hold is manually cleared.', icon: 'park' }, () => onOrderChanged?.());
  };


  const handleWithdraw = async () => {
    await updateOrder({ status: OrderStatus.Cancelled });
    await addComment({
      orderId: order.spId,
      authorName: 'System',
      authorEmail: 'system',
      commentText: `Order withdrawn by requester ${currentPerson.fullName}.`,
    });
    setShowWithdrawConfirm(false);
    showSuccessToast({ title: 'Order Withdrawn', sub: 'The order has been cancelled and retained for audit.', icon: 'withdraw' }, () => onOrderChanged?.());
  };

  const handleMarkPartialDelivery = async () => {
    await updateOrder({ status: OrderStatus.PartialDelivery });
    await addComment({
      orderId: order.spId,
      authorName: 'System',
      authorEmail: 'system',
      commentText: `Order marked as partial delivery by ${currentPerson.fullName}.`,
    });
    setShowPartialDeliveryConfirm(false);
    showSuccessToast({ title: 'Partial Delivery Marked', sub: 'Remaining items are still outstanding.', icon: 'partial' }, () => onOrderChanged?.());
  };

  const handleMarkDelivered = async () => {
    await updateOrder({ status: OrderStatus.Delivered, actualDeliveryDate: new Date().toISOString().split('T')[0] });
    await addComment({
      orderId: order.spId,
      authorName: 'System',
      authorEmail: 'system',
      commentText: `Order marked as delivered by ${currentPerson.fullName}.`,
    });
    setShowDeliveredConfirm(false);
    showSuccessToast({ title: 'Order Delivered!', sub: "All items received. Today's date recorded as delivery date.", icon: 'deliver' }, () => onOrderChanged?.());
  };

  return (
    <div className="ord-view-content">

      {/* ── Order Detail Header ──────────────────────────────────────────── */}
      <div className="ord-detail-hdr">
        <div>
          <div className="ord-detail-title">{order.orderRef} · {order.title}</div>
          <div className="ord-detail-sub">
            Raised by <strong>{order.requesterName}</strong> on {fmtDate(order.orderDate)} · Supplier: {order.supplier || '—'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <OrderStatusPill status={order.status} />
          {isOverdue(order) && <span style={{ color: 'var(--red)', fontWeight: 600, fontSize: 12 }}>⚠ Overdue</span>}
          <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginLeft: 4 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 14, height: 14 }}>
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
        </div>
      </div>

      {/* ── OM full-edit panel (toggled by Edit button) ─────────────────── */}
      {canToggleEdit && (
        <div className="ord-form-section" style={{ border: '1px solid var(--accent,#6366f1)', borderRadius: 8, padding: '16px 20px', marginBottom: 20, background: 'var(--surface)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent,#6366f1)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Order Details
            </div>
            {!omEditOpen && (
              <button
                className="btn btn-primary btn-sm"
                type="button"
                onClick={() => {
                  // Always re-seed from current saved values so stale edits don't bleed in
                  setDraftForm({
                    costAllocation: order.costAllocation,
                    projectCode: order.projectCode,
                    projectName: order.projectName,
                    supplier: order.supplier,
                    priority: order.priority,
                    currency: order.currency,
                    sterlingEquivalent: order.sterlingEquivalent != null ? String(order.sterlingEquivalent) : '',
                    requiredByDate: order.requiredByDate ?? '',
                    title: order.title,
                    notes: order.notes,
                  });
                  setDraftLineItems(lineItems.map(li => ({
                    id: li.id,
                    itemDescription: li.itemDescription,
                    sku: li.sku,
                    itemLink: li.itemLink,
                    quantity: li.quantity,
                    unitOfMeasure: li.unitOfMeasure,
                    unitPrice: li.unitPrice,
                    subtotal: li.subtotal,
                    isChemical: li.isChemical,
                    coshhRef: li.coshhRef,
                  })));
                  setDraftFyiViewers([...(order.fyiViewerEmails ?? [])]);
                  setDraftError(null);
                  setOMEditOpen(true);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit
              </button>
            )}
          </div>

          {omEditOpen && (
            <>
              <div style={{ marginTop: 16 }}>
                {renderDraftEditSections()}
              </div>
              {draftError && <div className="err-banner" style={{ marginBottom: 12 }}>{draftError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button
                  className="btn btn-ghost"
                  type="button"
                  disabled={isDraftSaving || isSaving}
                  onClick={() => {
                    setOMEditOpen(false);
                    setDraftError(null);
                    // Reset form back to current saved order values
                    draftInitializedRef.current = false;
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    if (isRequester && order.status === OrderStatus.ForReview) {
                      setShowNotifyOMConfirm(true);
                    } else {
                      handleOMFullSave();
                    }
                  }}
                  disabled={isDraftSaving || isSaving}
                >
                  {isDraftSaving && draftActiveAction === 'save'
                    ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
                    : 'Save Changes'
                  }
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Lock banner ─────────────────────────────────────────────────── */}
      {order.isLocked && order.status !== OrderStatus.Draft && (
        <div className="lock-banner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>Record locked after Line-Manager approval — only the <strong>OM Group</strong> can edit from here on.</span>
        </div>
      )}

      {/* ── Stage ribbon ────────────────────────────────────────────────── */}
      <div className="ord-stage-scroll-wrap">
        <div className="stage-ribbon">
          {stageLabels.map((s, i) => (
            <div key={i} className={`sg${s.cls ? ' ' + s.cls : ''}`}>
              <div className="sgl">{s.lbl}</div>
              <div className="sgw">{s.by}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Order meta strip ────────────────────────────────────────────── */}
      <div className="ord-detail-meta">
        <div>
          <div className="mlbl">CATEGORY</div>
          <div className="mval">
            {CAT_NAMES[order.costAllocation] ?? order.costAllocation}
            {order.projectCode && (
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text-dim)', marginTop: 3 }}>
                {order.projectCode}{order.projectName ? ` — ${order.projectName}` : ''}
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="mlbl">CURRENCY</div>
          <div className="mval" style={{ fontFamily: 'DM Mono, monospace' }}>
            {order.currency}
            {order.currency !== 'GBP' && order.sterlingEquivalent != null
              ? ` (£${order.sterlingEquivalent.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} sterling)`
              : ''}
          </div>
        </div>
        <div>
          <div className="mlbl">APPROVER</div>
          <div className="mval">
            {(() => {
              switch (order.status) {
                case OrderStatus.Requested:
                case OrderStatus.PendingEscalatedApproval:
                  return currentFinancialApprover?.name ?? resolvedLMName;
                case OrderStatus.PendingChemApproval:
                case OrderStatus.LMApproved:
                  return chemApproverName;
                case OrderStatus.PendingCEOApproval:
                  return isOBOCEOPerson ? currentPerson.fullName : (people?.find(p => p.canApproveOnBehalfOfCEO)?.fullName ?? 'CEO');
                case OrderStatus.PendingCFOApproval:
                  return resolvedCFOName;
                default:
                  return order.approvedOnBehalfOf ?? '—';
              }
            })()}
          </div>
        </div>
        <div>
          <div className="mlbl">TOTAL</div>
          <div className="mval">{viewCs}{subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        {order.expectedDeliveryDate && (
          <div>
            <div className="mlbl">EXPECTED DELIVERY</div>
            <div className="mval" style={{ fontFamily: 'DM Mono, monospace', color: isOverdue(order) ? 'var(--red)' : undefined }}>
              {fmtDate(order.expectedDeliveryDate)}{isOverdue(order) ? ' ⚠' : ''}
            </div>
          </div>
        )}
        {order.actualDeliveryDate && (
          <div>
            <div className="mlbl">ACTUAL DELIVERY</div>
            <div className="mval" style={{ fontFamily: 'DM Mono, monospace' }}>{fmtDate(order.actualDeliveryDate)}</div>
          </div>
        )}
        {order.poNumber && (
          <div>
            <div className="mlbl">PO NUMBER</div>
            <div className="mval" style={{ fontFamily: 'DM Mono, monospace' }}>{order.poNumber}</div>
          </div>
        )}
        <div>
          <div className="mlbl">COST CENTRE</div>
          <div className="mval">
            {order.costCentreName
              ? <>{order.costCentreName} <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>(assigned by OM)</span></>
              : <span style={{ color: 'var(--text-dim)' }}>Not yet assigned (OM Group)</span>}
          </div>
        </div>
        {order.fyiViewerEmails.length > 0 && (
          <div>
            <div className="mlbl">FYI VIEWERS</div>
            <div className="mval">{order.fyiViewerEmails.join(', ')}</div>
          </div>
        )}
      </div>

      {/* ── Line items ──────────────────────────────────────────────────── */}
      <div className="ord-form-section" style={{ marginTop: 20 }}>
        <div className="ord-form-title">Line items</div>

        {order.hasChemicalLines && (
          <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8 }}>
            ⚠ This order contains chemical items — COSHH approval required.
          </div>
        )}

        <div className="ord-li-scroll-wrap">
        <table className="li-table">
          <colgroup>
            <col style={{ width: '4%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>#</th>
              <th>Description</th>
              <th>SKU</th>
              <th style={{ textAlign: 'center' }}>Qty</th>
              <th style={{ textAlign: 'center' }}>UOM</th>
              <th style={{ textAlign: 'right' }}>Unit price</th>
              <th style={{ textAlign: 'right' }}>Line total</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: 16 }}>No line items</td></tr>
            ) : (
              lineItems.map((li, idx) => (
                <tr key={li.id}>
                  <td style={{ textAlign: 'center', color: 'var(--text-dim)', fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{idx + 1}</td>
                  <td>
                    {li.itemDescription}
                    {li.isChemical && li.coshhRef && (() => {
                      const entry = draftCOSHH.find(c => c.coshhRef === li.coshhRef);
                      return (
                        <div className="coshh-badge-wrap">
                          <span className="coshh-badge">
                            ⚠ {li.coshhRef}{entry ? ` · ${entry.chemicalName} · ${entry.hazardType}` : ''}
                          </span>
                          {entry?.description && (
                            <div className="coshh-description">{entry.description}</div>
                          )}
                        </div>
                      );
                    })()}
                    {li.itemLink && looksLikeUrl(li.itemLink) && (
                      <div className="li-link-bubble li-link-readonly">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                        <a href={normaliseUrl(li.itemLink)} target="_blank" rel="noopener noreferrer" title={li.itemLink}>{li.itemLink.length > 60 ? li.itemLink.slice(0, 57) + '…' : li.itemLink}</a>
                      </div>
                    )}
                  </td>
                  <td style={{ color: li.sku ? 'inherit' : 'var(--text-dim)', fontFamily: 'DM Mono, monospace', fontSize: 12 }}>{li.sku || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{li.quantity}</td>
                  <td style={{ textAlign: 'center', color: li.unitOfMeasure ? 'inherit' : 'var(--text-dim)' }}>{li.unitOfMeasure || '—'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{viewCs}{li.unitPrice.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>{viewCs}{(li.quantity * li.unitPrice).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="li-totals">
          <div>
            <span className="lbl">Total</span>
            <span className="val grand">
              {viewCs}{subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {order.currency !== 'GBP' && order.sterlingEquivalent != null && (
                <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 6 }}>(£{order.sterlingEquivalent.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GBP)</span>
              )}
            </span>
          </div>
        </div>
        </div>{/* ord-li-scroll-wrap */}
      </div>

      {/* ── Attachments ─────────────────────────────────────────────────── */}
      <div className="ord-form-section">
        <div className="ord-form-title">Attachments</div>
        {attachments.length === 0 && pendingFiles.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '6px 0' }}>No attachments uploaded yet.</div>
        ) : (
          <div className="attach-list">
            {attachments.map(att => (
              <div className="attach-row" key={att.serverRelUrl}>
                <svg className="fi" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span className="atname">{att.name}</span>
                <span className="atmeta">{att.category === 'ceo-approval' ? 'CEO Approval' : att.category === 'quote' ? 'Quote' : 'File'}</span>
                <button className="atdel" title="Download" onClick={() => downloadAttachment(att)}>↓</button>
              </div>
            ))}
          </div>
        )}
        {canUploadAttachment && pendingFiles.length > 0 && (
          <div className="attach-list" style={{ marginTop: 6 }}>
            {pendingFiles.map((f, idx) => (
              <div className="attach-row" key={`${f.name}-${idx}`}>
                <svg className="fi" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span className="atname" style={{ color: 'var(--text-muted)' }}>{f.name}</span>
                <span className="atmeta" style={{ color: 'var(--accent)' }}>Pending</span>
                <button className="atdel" title="Remove" onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}>✕</button>
              </div>
            ))}
          </div>
        )}
        {canUploadAttachment && (
          <div style={{ marginTop: 10 }}>
            <div
              className={`attach-drop${isDragOver ? ' drag-over' : ''}`}
              onClick={() => attachFileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDragEnter={e => { e.preventDefault(); dragCounterRef.current += 1; setIsDragOver(true); }}
              onDragLeave={() => { dragCounterRef.current -= 1; if (dragCounterRef.current === 0) setIsDragOver(false); }}
              onDrop={e => {
                e.preventDefault();
                e.stopPropagation();
                dragCounterRef.current = 0;
                setIsDragOver(false);
                const files = Array.from(e.dataTransfer.files);
                if (files.length) setPendingFiles(prev => [...prev, ...files]);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span>{isDragOver ? 'Drop files here' : 'Drop files here or click to browse'}</span>
              <div className="attach-hint">PDF, Word, Excel, PNG, JPG</div>
            </div>
            <input
              ref={attachFileRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={e => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) setPendingFiles(prev => [...prev, ...files]);
                e.target.value = '';
              }}
            />
            {pendingFiles.length > 0 && (
              <button
                className="btn btn-primary btn-sm"
                style={{ marginTop: 8 }}
                disabled={isUploading || isSaving}
                onClick={async () => {
                  setIsUploading(true);
                  try {
                    for (const f of pendingFiles) await uploadAttachment(order, f, 'other');
                    setPendingFiles([]);
                  } finally {
                    setIsUploading(false);
                  }
                }}
              >
                {isUploading ? 'Uploading…' : `Upload ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── FYI Viewer button + panel ────────────────────────────────────── */}
      {/* Editable for current stage approvers: requester, LM, OM group, chem approver */}
      {(isRequester || isOMGroup || isChemApprover || isLM) && (
        <div className="ord-form-section">
          <div className="ord-form-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 15, height: 15, flexShrink: 0 }}>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
            </svg>
            FYI Viewers
          </div>
          {order.fyiViewerEmails.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {order.fyiViewerEmails.map(fyi => {
                const fp = (people ?? []).find(x => x.m365Email.toLowerCase() === fyi.toLowerCase());
                return (
                  <span key={fyi} className="fyi-chip">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 11, height: 11, flexShrink: 0 }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                    {fp ? fp.fullName : fyi}
                    <button
                      className="fyi-chip-del"
                      type="button"
                      title={`Remove ${fyi}`}
                      onClick={() => setConfirmDeleteFYIEmail(fyi)}
                    >×</button>
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setFyiPickerSelected([...order.fyiViewerEmails]); setFyiPickerSearch(''); setShowFYIPickerLive(true); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Select People
            </button>
          </div>
        </div>
      )}

      {/* Read-only FYI Viewers for everyone else with order access */}
      {hasOrderAccess && !isRequester && !isOMGroup && !isChemApprover && !isLM && order.fyiViewerEmails.length > 0 && (
        <div className="ord-form-section">
          <div className="ord-form-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 15, height: 15, flexShrink: 0 }}>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
            </svg>
            FYI Viewers
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {order.fyiViewerEmails.map(fyi => {
              const fp = (people ?? []).find(x => x.m365Email.toLowerCase() === fyi.toLowerCase());
              return (
                <span key={fyi} className="fyi-chip">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 11, height: 11, flexShrink: 0 }}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                  </svg>
                  {fp ? fp.fullName : fyi}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ── FYI People Picker Modal ──────────────────────────────────────── */}
      {showFYIPickerLive && <FYIPickerModal people={people ?? []} selected={fyiPickerSelected} search={fyiPickerSearch} onSearchChange={setFyiPickerSearch} onToggle={email => setFyiPickerSelected(prev => prev.map(e=>e.toLowerCase()).includes(email.toLowerCase()) ? prev.filter(e=>e.toLowerCase()!==email.toLowerCase()) : [...prev,email])} onClose={() => setShowFYIPickerLive(false)} onConfirm={() => handleAddFYI(fyiPickerSelected)} isSaving={isSaving} />}

      {/* ── Delete FYI Viewer confirmation modal ────────────────────────── */}
      {confirmDeleteFYIEmail && (
        <div className="li-del-overlay" onClick={() => setConfirmDeleteFYIEmail(null)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            </div>
            <div className="li-del-modal-title">Remove FYI Viewer?</div>
            <div className="li-del-modal-desc">
              <span className="li-del-modal-item">{confirmDeleteFYIEmail}</span>
              <br />will no longer have visibility of this order.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" type="button" onClick={() => setConfirmDeleteFYIEmail(null)}>
                Cancel
              </button>
              <button
                className="li-del-modal-confirm"
                type="button"
                disabled={isSaving}
                onClick={handleDeleteFYI}
              >
                {isSaving
                  ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Removing…</>
                  : <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                      Yes, remove them
                    </>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approval action panel ────────────────────────────────────────── */}
      {canApprove && (
        <div className="ord-approve-panel" style={{ marginBottom: 20, ...(canCEOOBOApprove ? { borderLeftColor: '#d97706' } : {}) }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Your Decision — {approvalStageLabel}</div>

          {canCEOOBOApprove && (
            <>
              <div className="ord-ceo-banner" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 16, height: 16, flexShrink: 0 }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>
                  This order meets or exceeds £{ORDER_CEO_CFO_THRESHOLD.toLocaleString()} and requires CEO approval.
                  Upload the CEO's approval email as a PDF.
                </span>
              </div>
              <div className="form-field" style={{ marginBottom: 12 }}>
                <label>CEO approval email (PDF / .msg / .eml)</label>
                <input
                  type="file"
                  accept=".pdf,.msg,.eml"
                  className="finput ord-file-input"
                  style={{ marginTop: 4 }}
                  onChange={e => setCeoEmailFile(e.target.files?.[0] ?? null)}
                />
                {ceoEmailFile && (
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>📎 {ceoEmailFile.name}</div>
                )}
              </div>
            </>
          )}


          <textarea
            className="finput"
            rows={3}
            style={{ resize: 'vertical', marginBottom: 8 }}
            placeholder={canOMProcess ? 'Note (optional)…' : 'Note (required for rejection, optional for approval)…'}
            value={approvalNote}
            onChange={e => setApprovalNote(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-success"
              onClick={handleApprove}
              disabled={isSaving}
            >
              {isSaving
                ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Saving…</>
                : canOMProcess ? 'Mark as Ordered' : 'Approve'
              }
            </button>
            {!canOMProcess && (
              <button
                className="btn btn-ghost"
                style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                onClick={handleReject}
                disabled={isSaving || !approvalNote.trim()}
                title={!approvalNote.trim() ? 'A rejection note is required' : undefined}
              >
                Reject
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── OM Group editing panel ───────────────────────────────────────── */}
      {canEditOMFields && (
        <div className="ord-form-section">
          <div className="ord-form-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 15, height: 15, flexShrink: 0 }}>
              <path d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5" /><path d="M17.586 3.586a2 2 0 1 1 2.828 2.828L12 15l-4 1 1-4z" />
            </svg>
            OM Fields
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: showOMPanel ? 8 : 0 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowOMPanel(v => !v)}>
              {showOMPanel ? 'Close OM Fields' : 'Edit OM Fields'} (PO, Cost Centre, Delivery Dates)
            </button>
            {order.status === OrderStatus.Ordered && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: 'var(--accent)', borderColor: 'rgba(99,102,241,0.35)' }}
                onClick={() => setShowPartialDeliveryConfirm(true)}
                title="Some items received, more expected"
              >
                Mark Partial Delivery
              </button>
            )}
            {(order.status === OrderStatus.Ordered || order.status === OrderStatus.PartialDelivery) && (
              <button
                className="btn btn-success btn-sm"
                onClick={() => setShowDeliveredConfirm(true)}
                title="All items received — close the order"
              >
                Mark Delivered
              </button>
            )}
            {!isLM && order.status !== OrderStatus.Cancelled && order.status !== OrderStatus.Delivered && order.status !== OrderStatus.ForReview && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: '#b45309', borderColor: 'rgba(180,83,9,0.35)' }}
                onClick={() => setShowForReviewConfirm(true)}
                title="Park this order — no action until cleared"
              >
                Mark for Review
              </button>
            )}
            {!isLM && order.status !== OrderStatus.Cancelled && order.status !== OrderStatus.Delivered && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: 'var(--red)', borderColor: 'rgba(220,38,38,0.3)' }}
                onClick={() => setShowCancelConfirm(true)}
                title="Cancel this order — record retained for audit"
              >
                Cancel Order
              </button>
            )}
          </div>
          {showOMPanel && (
            <div className="ord-om-panel">
              <div className="ord-grid-2" style={{ marginBottom: 12 }}>
                <div className="form-field">
                  <label>PO Number</label>
                  <input className="finput" value={omFields.poNumber || order.poNumber || ''} placeholder="e.g. PO-2026-001"
                    onChange={e => setOMFields(f => ({ ...f, poNumber: e.target.value }))} />
                </div>
                <div className="form-field">
                  <label>Cost Centre</label>
                  <select className="finput" value={omFields.costCentreName || order.costCentreName || ''}
                    onChange={e => setOMFields(f => ({ ...f, costCentreName: e.target.value }))}>
                    <option value="">— Select Cost Centre —</option>
                    {costCentreOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Expected Delivery Date</label>
                  <DatePickerInput
                    value={omFields.expectedDeliveryDate || order.expectedDeliveryDate || ''}
                    onChange={v => setOMFields(f => ({ ...f, expectedDeliveryDate: v }))} />
                </div>
                <div className="form-field">
                  <label>Actual Delivery Date</label>
                  <DatePickerInput
                    value={omFields.actualDeliveryDate || order.actualDeliveryDate || ''}
                    onChange={v => setOMFields(f => ({ ...f, actualDeliveryDate: v }))} />
                </div>
                {order.currency !== 'GBP' && (
                  <div className="form-field">
                    <label>Sterling Equivalent (£)</label>
                    <input type="number" className="finput" step="0.01"
                      value={omFields.sterlingEquivalent || (order.sterlingEquivalent != null ? String(order.sterlingEquivalent) : '')}
                      onChange={e => setOMFields(f => ({ ...f, sterlingEquivalent: e.target.value }))} />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={handleOMSave} disabled={isSaving}>Save OM Fields</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowOMPanel(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── OM Fields read-only view (non-OM-group users) ───────────────── */}
      {!canEditOMFields && (
        <div className="ord-form-section">
          <div className="ord-form-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 15, height: 15, flexShrink: 0 }}>
              <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            OM Fields
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowOMPanel(v => !v)}>
            {showOMPanel ? 'Close OM Fields' : 'View OM Fields'} (PO, Cost Centre, Delivery Dates)
          </button>
          {showOMPanel && (
            <div className="ord-om-panel" style={{ marginTop: 8 }}>
              <div className="ord-grid-2" style={{ marginBottom: 8 }}>
                <div>
                  <div className="mlbl">PO NUMBER</div>
                  <div className="mval">{order.poNumber || '—'}</div>
                </div>
                <div>
                  <div className="mlbl">COST CENTRE</div>
                  <div className="mval">{order.costCentreName || '—'}</div>
                </div>
                <div>
                  <div className="mlbl">EXPECTED DELIVERY</div>
                  <div className="mval">{fmtDate(order.expectedDeliveryDate)}</div>
                </div>
                <div>
                  <div className="mlbl">ACTUAL DELIVERY</div>
                  <div className="mval">{fmtDate(order.actualDeliveryDate)}</div>
                </div>
                {order.currency !== 'GBP' && (
                  <div>
                    <div className="mlbl">STERLING EQUIVALENT</div>
                    <div className="mval">{order.sterlingEquivalent != null ? `£${order.sterlingEquivalent.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Requester withdraw (Draft or Requested, before lock) ────────── */}
      {isRequester && !order.isLocked &&
       (order.status === OrderStatus.Draft || order.status === OrderStatus.Requested) && (
        <div style={{ marginBottom: 16 }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--red)', borderColor: 'rgba(220,38,38,0.3)' }}
            onClick={() => setShowWithdrawConfirm(true)}
          >
            Withdraw Order
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-dim)', marginLeft: 10 }}>
            Withdraws this order before approval. Record retained for audit.
          </span>
        </div>
      )}

      {/* ── Cancel Order confirmation modal ─────────────────────────────── */}
      {showCancelConfirm && (
        <div className="li-del-overlay" onClick={() => setShowCancelConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" />
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
              </svg>
            </div>
            <div className="li-del-modal-title">Cancel this order?</div>
            <div className="li-del-modal-desc">
              <span className="li-del-modal-item">{order.orderRef} — {order.title}</span>
              <br />will be marked as Cancelled. The record is retained for audit purposes.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowCancelConfirm(false)}>Keep Order</button>
              <button
                className="li-del-modal-confirm"
                disabled={isSaving}
                onClick={handleCancelOrder}
              >
                {isSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Cancelling…</> : 'Yes, Cancel It'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── For Review confirmation modal ────────────────────────────────── */}
      {showForReviewConfirm && (
        <div className="li-del-overlay" onClick={() => setShowForReviewConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon" style={{ background: 'rgba(180,83,9,0.1)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="li-del-modal-title">Park as "For Review"?</div>
            <div className="li-del-modal-desc">
              <span className="li-del-modal-item">{order.orderRef} — {order.title}</span>
              <br />will be marked with no action until manually cleared. Use this for queries or holds.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowForReviewConfirm(false)}>Keep Status</button>
              <button
                className="li-del-modal-confirm"
                style={{ background: '#b45309' }}
                disabled={isSaving}
                onClick={handleMarkForReview}
              >
                {isSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Saving…</> : 'Mark for Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Notify OM confirmation modal (requester saving from For Review) ── */}
      {showNotifyOMConfirm && (
        <div className="li-del-overlay" onClick={() => setShowNotifyOMConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon" style={{ background: 'rgba(99,102,241,0.1)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent,#6366f1)" strokeWidth="1.8">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <div className="li-del-modal-title">Notify OM Group?</div>
            <div className="li-del-modal-desc">
              Your changes will be saved. Do you want to notify the OM group so they can continue processing this order?
              <br /><br />
              <strong>Save &amp; Notify</strong> — saves changes, notifies OM group, and moves the order back to OM processing.
              <br />
              <strong>Save Only</strong> — saves changes but keeps the order in "For Review".
            </div>
            <div className="li-del-modal-actions">
              <button
                className="btn btn-ghost"
                disabled={isDraftSaving}
                onClick={() => handleOMFullSave(false)}
              >
                Save Only
              </button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--accent,#6366f1)', borderColor: 'var(--accent,#6366f1)' }}
                disabled={isDraftSaving}
                onClick={() => handleOMFullSave(true)}
              >
                {isDraftSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Saving…</> : 'Save & Notify OM'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Submit confirmation modal ─────────────────────────────────────── */}
      {showSubmitConfirm && (
        <div className="li-del-overlay" onClick={() => setShowSubmitConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon" style={{ background: 'rgba(34,197,94,0.1)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--success,#22c55e)" strokeWidth="1.8" style={{ width: 24, height: 24 }}>
                <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" />
              </svg>
            </div>
            <div className="li-del-modal-title">Submit for Approval?</div>
            <div className="li-del-modal-desc">
              Once submitted, your order will be sent for approval. You won't be able to edit it until a decision is made.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowSubmitConfirm(false)}>Cancel</button>
              <button
                className="li-del-modal-confirm"
                style={{ background: 'var(--success,#22c55e)' }}
                disabled={isDraftSaving}
                onClick={() => { setShowSubmitConfirm(false); submitConfirmFnRef.current?.(); }}
              >
                {isDraftSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Submitting…</> : 'Yes, Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Withdraw confirmation modal (requester) ──────────────────────── */}
      {showWithdrawConfirm && (
        <div className="li-del-overlay" onClick={() => setShowWithdrawConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" />
              </svg>
            </div>
            <div className="li-del-modal-title">Withdraw this order?</div>
            <div className="li-del-modal-desc">
              <span className="li-del-modal-item">{order.orderRef} — {order.title}</span>
              <br />will be withdrawn and marked as Cancelled. The record is retained for audit.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowWithdrawConfirm(false)}>Keep Order</button>
              <button
                className="li-del-modal-confirm"
                disabled={isSaving}
                onClick={handleWithdraw}
              >
                {isSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Withdrawing…</> : 'Yes, Withdraw'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Partial Delivery confirmation modal ────────────────────────── */}
      {showPartialDeliveryConfirm && (
        <div className="li-del-overlay" onClick={() => setShowPartialDeliveryConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon" style={{ background: 'rgba(99,102,241,0.1)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8">
                <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
              </svg>
            </div>
            <div className="li-del-modal-title">Mark as Partial Delivery?</div>
            <div className="li-del-modal-desc">
              <span className="li-del-modal-item">{order.orderRef} — {order.title}</span>
              <br />Some items received, remainder still outstanding.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowPartialDeliveryConfirm(false)}>Cancel</button>
              <button
                className="li-del-modal-confirm"
                style={{ background: 'var(--accent)' }}
                disabled={isSaving}
                onClick={handleMarkPartialDelivery}
              >
                {isSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Saving…</> : 'Confirm Partial'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delivered confirmation modal ─────────────────────────────────── */}
      {showDeliveredConfirm && (
        <div className="li-del-overlay" onClick={() => setShowDeliveredConfirm(false)}>
          <div className="li-del-modal" onClick={e => e.stopPropagation()}>
            <div className="li-del-modal-icon" style={{ background: 'rgba(22,163,74,0.1)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="1.8">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="li-del-modal-title">Mark as Delivered?</div>
            <div className="li-del-modal-desc">
              <span className="li-del-modal-item">{order.orderRef} — {order.title}</span>
              <br />All items received. Today's date will be recorded as actual delivery date.
            </div>
            <div className="li-del-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowDeliveredConfirm(false)}>Cancel</button>
              <button
                className="li-del-modal-confirm"
                style={{ background: 'var(--green)' }}
                disabled={isSaving}
                onClick={handleMarkDelivered}
              >
                {isSaving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Saving…</> : 'Confirm Delivered'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Comments & audit trail (unified) ────────────────────────────── */}
      <div className="comment-thread">
        <div className="ct-hdr">
          Comments &amp; Audit Trail
          <span className="ct-count">{unifiedTimeline.length} entr{unifiedTimeline.length !== 1 ? 'ies' : 'y'}</span>
        </div>
        <div className="ct-list">
          {unifiedTimeline.length === 0 && (
            <div className="ct-item" style={{ color: 'var(--text-dim)', fontSize: 12 }}>No activity yet.</div>
          )}
          {unifiedTimeline.map((entry, i) => {
            if (entry.kind === 'system') {
              return (
                <div key={`sys-${i}`} className="ct-item ct-item-system">
                  <div className="ct-hdr-row">
                    <span className="ct-role ct-sys">System</span>
                    <span className="ct-ts">{fmtDateTime(entry.ts)}</span>
                  </div>
                  <div className="ct-body ct-body-muted">{entry.text}</div>
                </div>
              );
            }
            if (entry.kind === 'approval') {
              const log = entry.log;
              const approved = log.decision === ApprovalDecision.Approved;
              return (
                <div key={log.id} className={`ct-item${approved ? ' ct-item-approved' : ' ct-item-rejected'}`}>
                  <div className="ct-hdr-row">
                    <span className={`ct-role ${approved ? 'ct-app' : 'ct-rej'}`}>
                      {log.approverName}
                      {log.onBehalfOf && <span style={{ fontWeight: 400 }}> · on behalf of {log.onBehalfOf}</span>}
                    </span>
                    <span className="ct-ts">{fmtDateTime(log.approvalDate)}</span>
                  </div>
                  <div className="ct-body">
                    <span className={approved ? 'ord-log-approved' : 'ord-log-rejected'}>{log.decision}</span>
                    <span style={{ color: 'var(--text-dim)', fontSize: 12 }}> · {log.approvalStage}</span>
                    {log.note && <span style={{ fontStyle: 'italic' }}> — {log.note}</span>}
                  </div>
                </div>
              );
            }
            const c = entry.comment;
            const isReq = c.authorEmail.toLowerCase() === order.requesterEmail.toLowerCase();
            const isSys = c.authorName === 'System' || c.authorEmail === 'system';
            const roleClass = isSys ? 'ct-sys' : isReq ? 'ct-req' : 'ct-app';
            return (
              <div key={c.id} className={`ct-item${isSys ? ' ct-item-system' : ''}`}>
                <div className="ct-hdr-row">
                  <span className={`ct-role ${roleClass}`}>{c.authorName}</span>
                  <span className="ct-ts">{fmtDateTime(c.commentDate)}</span>
                </div>
                <div className={`ct-body${isSys ? ' ct-body-muted' : ''}`}>{c.commentText}</div>
              </div>
            );
          })}
        </div>
        {hasOrderAccess && !isFYIOnlyViewer && (
          <div className="ct-input-row">
            <textarea
              placeholder="Add a comment... (all parties are notified)"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
            />
            <button
              className="btn btn-primary"
              onClick={handleComment}
              disabled={isSaving || !commentText.trim()}
            >
              Post
            </button>
          </div>
        )}
      </div>

      {/* ── Success toast ───────────────────────────────────────────────────── */}
      {successToast && (
        <div className="ord-success-overlay">
          <div className="ord-success-toast">
            <div className={`ord-success-icon ord-success-icon--${successToast.icon}`}>
              {successToast.icon === 'check' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              )}
              {successToast.icon === 'submit' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4z"/></svg>
              )}
              {successToast.icon === 'reject' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
              )}
              {successToast.icon === 'park' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              )}
              {(successToast.icon === 'cancel' || successToast.icon === 'withdraw') && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
              )}
              {successToast.icon === 'partial' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z"/></svg>
              )}
              {successToast.icon === 'deliver' && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              )}
            </div>
            <div className="ord-success-title">{successToast.title}</div>
            <div className="ord-success-sub">{successToast.sub}</div>
            <button className="ord-success-ok" onClick={dismissSuccessToast}>Ok</button>
          </div>
        </div>
      )}

    </div>
  );
}
